<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="visible"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40"
        @click.self="close"
      >
        <div
          class="relative w-[90vw] md:w-[70vw] lg:w-[60vw] max-w-4xl max-h-[90vh] mx-auto rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden"
        >
          <!-- Header -->
          <header
            class="flex items-start justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0"
          >
            <div>
              <h2 class="text-lg font-semibold text-black">Fiche produit</h2>
              <p v-if="product" class="mt-1 text-xs text-black">
                {{ product.reference }} · ID #{{ product.id }}
                <span v-if="formattedDate"> · {{ formattedDate }}</span>
              </p>
            </div>
            <button
              type="button"
              class="inline-flex h-8 w-8 items-center justify-center rounded-full text-black hover:bg-gray-100 hover:text-black"
              @click="close"
            >
              <span class="sr-only">Fermer</span>
              <i class="fas fa-times text-sm" />
            </button>
          </header>

          <!-- Body -->
          <div
            v-if="localProduct"
            class="flex-1 overflow-y-auto px-6 py-5 space-y-6"
          >
            <!-- ========== TOP : Aperçu + stock couleurs ========== -->
            <section class="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <!-- 折叠头部 -->
              <div class="flex items-center justify-between mb-3">
                <h3 class="text-sm font-semibold text-black">
                  Aperçu & stock couleurs
                </h3>

                <button
                  type="button"
                  class="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                  @click="isTopOpen = !isTopOpen"
                >
                  <i
                    class="fas"
                    :class="isTopOpen ? 'fa-chevron-up' : 'fa-chevron-down'"
                  />
                  <span>{{ isTopOpen ? "Réduire" : "Afficher" }}</span>
                </button>
              </div>

              <!-- 注意：这里用 v-if（或 v-show），只包裹下面这一大块内容 -->
              <div v-if="isTopOpen" class="flex flex-col gap-4">
                <!-- Référence, marque, prix au-dessus des photos -->
                <div class="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                  <h3
                    class="text-lg font-semibold text-blue-600 uppercase tracking-wide"
                  >
                    {{ localProduct.reference }}
                  </h3>
                  <p class="text-sm text-black">
                    {{ localProduct.marque || "Votre marque" }}
                  </p>
                  <p class="text-xl font-bold text-black">
                    {{ localProduct.prix?.toFixed(2) }}€
                  </p>
                </div>

                <div class="flex flex-col md:flex-row gap-6 md:gap-0">
                  <div
                    class="w-full md:w-1/3 min-w-0 flex flex-col items-center gap-3"
                  >
                    <div
                      class="relative w-full max-w-[260px] md:max-w-[300px] mx-auto aspect-[3/4] rounded-2xl bg-gray-100 overflow-hidden flex items-center justify-center p-2 cursor-pointer"
                      @touchstart="onTouchStart"
                      @touchend="onTouchEnd"
                      @keydown.left.prevent="prevImage"
                      @keydown.right.prevent="nextImage"
                      tabindex="0"
                      @click="openLightbox"
                    >
                      <!-- Single image preview only: remove stacked background to prevent overlap -->
                      <img
                        v-if="currentImage"
                        :src="getDisplayPhotoUrl(currentImage)"
                        :alt="localProduct.reference"
                        class="w-full h-full object-contain select-none rounded-2xl transition-transform duration-200"
                        :style="{
                          transform: `rotate(${getPendingRotationDegrees(
                            currentImage,
                          )}deg)`,
                        }"
                        @error="onImageError"
                      />
                      <span v-else class="text-xs text-black">
                        Aucune photo
                      </span>

                      <!-- Navigation arrows -->
                      <button
                        v-if="localImages.length > 1"
                        type="button"
                        class="absolute left-1 top-1/2 -translate-y-1/2 bg-white/70 hover:bg-white text-black rounded-full w-7 h-7 flex items-center justify-center shadow"
                        @click.stop="prevImage"
                      >
                        <i class="fas fa-chevron-left text-xs" />
                      </button>
                      <button
                        v-if="localImages.length > 1"
                        type="button"
                        class="absolute right-1 top-1/2 -translate-y-1/2 bg-white/70 hover:bg-white text-black rounded-full w-7 h-7 flex items-center justify-center shadow"
                        @click.stop="nextImage"
                      >
                        <i class="fas fa-chevron-right text-xs" />
                      </button>
                    </div>

                    <div
                      v-if="localImages.length || isLoadingImages"
                      class="flex gap-2 mt-4 overflow-x-auto overflow-y-hidden p-3 w-full min-w-0 justify-start items-center scroll-smooth"
                      style="-webkit-overflow-scrolling: touch"
                    >
                      <div
                        v-for="(img, idx) in localImages"
                        :key="img"
                        class="relative group flex-shrink-0 w-28 h-28 rounded-lg overflow-hidden bg-gray-100"
                        :class="[
                          idx === activeImageIndex
                            ? 'ring-2 ring-blue-500 border-2 border-blue-500'
                            : 'border border-gray-200',
                          isDisplayCover(idx) && idx !== activeImageIndex
                            ? 'border-2 border-blue-500'
                            : '',
                          'cursor-move',
                          draggingPhotoIndex === idx
                            ? 'opacity-50 scale-95'
                            : '',
                          dragOverPhotoIndex === idx
                            ? 'ring-2 ring-blue-400 ring-offset-1'
                            : '',
                        ]"
                        :draggable="true"
                        @click="
                          togglePhotoOverlay(idx);
                          activeImageIndex = idx;
                        "
                        @dragstart="handleThumbDragStart($event, idx)"
                        @dragend="handleThumbDragEnd()"
                        @dragover="handleThumbDragOver($event, idx)"
                        @dragleave="handleThumbDragLeave()"
                        @drop="handleThumbDrop($event, idx)"
                      >
                        <img
                          :src="getDisplayPhotoUrl(img)"
                          alt=""
                          class="w-full h-full object-contain rounded-lg pointer-events-none transition-transform duration-200"
                          :style="{
                            transform: `rotate(${getPendingRotationDegrees(
                              img,
                            )}deg)`,
                          }"
                          @error="onImageError"
                        />
                        <!-- Badge photo de couverture (première de la liste) -->
                        <div
                          v-if="isDisplayCover(idx)"
                          class="absolute top-0 left-0 right-0 bg-blue-500 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded-b text-center leading-tight pointer-events-none"
                        >
                          Photo de couverture
                        </div>
                        <!-- Overlay au survol : mêmes boutons que gestion des photos avancée -->
                        <div
                          :class="[
                            'absolute inset-0 bg-black/50 transition-opacity grid grid-cols-2 gap-1 items-center justify-center content-center p-1',
                            isPhotoOverlayActive(idx)
                              ? 'opacity-100 pointer-events-auto'
                              : 'opacity-0 pointer-events-none',
                            'sm:opacity-0 sm:pointer-events-none sm:group-hover:opacity-100 sm:group-hover:pointer-events-auto',
                          ]"
                        >
                          <!-- Définir comme photo de couverture (uniquement si pas déjà en première) -->
                          <button
                            v-if="!isDisplayCover(idx)"
                            type="button"
                            class="p-2 bg-white/90 text-yellow-500 rounded-full hover:bg-white focus:outline-none disabled:opacity-60 transition-transform hover:scale-90"
                            title="Définir comme photo de couverture"
                            :disabled="
                              isPhotoActionLoading(
                                getPhotoFilename(img),
                                'setCover',
                              )
                            "
                            @click.stop="handleSetAsCover(img)"
                          >
                            <i class="fas fa-star text-sm"></i>
                          </button>
                          <button
                            type="button"
                            class="p-2 bg-white/90 text-blue-600 rounded-full hover:bg-white focus:outline-none disabled:opacity-60 transition-transform hover:scale-90"
                            title="Pivoter la photo"
                            :disabled="
                              isPhotoActionLoading(
                                getPhotoFilename(img),
                                'rotate',
                              )
                            "
                            @click.stop="handleRotatePhoto(img)"
                          >
                            <i class="fas fa-redo-alt text-sm"></i>
                          </button>
                          <button
                            v-if="canDownloadPhotos"
                            type="button"
                            class="p-2 bg-white/90 text-black rounded-full hover:bg-white focus:outline-none transition-transform hover:scale-90 flex items-center justify-center"
                            title="Télécharger cette photo"
                            @click.stop="handleDownloadSinglePhoto(img)"
                          >
                            <i class="fas fa-download text-sm"></i>
                          </button>
                          <!-- Suppression autorisée pour toutes les photos si brouillon (premel='1') -->
                          <button
                            v-if="
                              !isDisplayCover(idx) ||
                              localProduct?.premel === '1'
                            "
                            type="button"
                            class="p-2 bg-white/90 text-red-600 rounded-full hover:bg-white focus:outline-none disabled:opacity-60 transition-transform hover:scale-90"
                            title="Supprimer la photo"
                            :disabled="
                              isPhotoActionLoading(
                                getPhotoFilename(img),
                                'delete',
                              )
                            "
                            @click.stop="handleDeletePhoto(img, idx)"
                          >
                            <i class="fas fa-trash-alt text-sm"></i>
                          </button>
                        </div>
                      </div>
                      <!-- Loading indicator for additional images -->
                      <div
                        v-if="isLoadingImages"
                        class="flex items-center gap-2 text-black text-xs ml-2"
                      >
                        <i class="fas fa-spinner fa-spin"></i>
                        <span>Chargement...</span>
                      </div>
                    </div>

                    <div
                      v-if="productIdForPhotos != null"
                      class="mt-4 flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800"
                    >
                      <i class="fas fa-info-circle mt-0.5 flex-shrink-0"></i>
                      <span>
                        Si vous utilisez des photos IA, ajoutez également au
                        moins une photo réelle du produit sur cintre.
                      </span>
                    </div>

                    <!-- Zone d'upload (même que gestion des upload photos) -->
                    <div
                      v-if="productIdForPhotos != null"
                      :class="[
                        'border-2 border-dashed rounded-lg p-3 text-center transition-all duration-200 mt-2',
                        isDragOverUpload
                          ? 'border-blue-500 bg-blue-50 shadow-md cursor-grabbing'
                          : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/50 cursor-pointer bg-white',
                      ]"
                      @click="triggerUploadFileDialog"
                      @dragenter.prevent="handleUploadDragEnter"
                      @dragover.prevent="handleUploadDragOver"
                      @dragleave.prevent="handleUploadDragLeave"
                      @drop.prevent="handleUploadDrop"
                    >
                      <div
                        class="flex flex-col items-center gap-2 justify-center min-h-[60px]"
                      >
                        <i
                          class="fas fa-cloud-upload-alt text-2xl transition-transform"
                          :class="
                            isDragOverUpload
                              ? 'text-blue-500 scale-110'
                              : 'text-black'
                          "
                        />
                        <span
                          v-if="isUploadingPhotos"
                          class="flex items-center gap-2 text-xs text-blue-600 font-medium"
                        >
                          <span
                            class="inline-block w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"
                          />
                          Téléversement en cours...
                        </span>
                        <template v-else>
                          <span
                            class="text-xs font-medium block"
                            :class="
                              isDragOverUpload ? 'text-blue-600' : 'text-black'
                            "
                          >
                            {{
                              isDragOverUpload
                                ? "Déposez les photos ici"
                                : "Glissez-déposez ou cliquez"
                            }}
                          </span>
                        </template>
                        <span
                          v-if="localImages.length > 0"
                          class="text-2xs font-medium"
                          :class="
                            isDragOverUpload ? 'text-blue-600' : 'text-black'
                          "
                        >
                          {{ localImages.length }}
                          photo{{ localImages.length > 1 ? "s" : "" }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- ========== DONNÉES DE LA RÉFÉRENCE ========== -->
            <!-- ========== DONNÉES DE LA RÉFÉRENCE ========== -->
            <section
              class="rounded-xl border border-gray-200 bg-gray-50/60 p-4"
            >
              <!-- 折叠头部 -->
              <div class="flex items-center justify-between mb-3">
                <h3 class="text-sm font-semibold text-black">
                  Données de la référence
                </h3>

                <button
                  type="button"
                  class="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                  @click="isReferenceOpen = !isReferenceOpen"
                >
                  <i
                    class="fas"
                    :class="
                      isReferenceOpen ? 'fa-chevron-up' : 'fa-chevron-down'
                    "
                  />
                  <span>{{ isReferenceOpen ? "Réduire" : "Afficher" }}</span>
                </button>
              </div>

              <!-- 被折叠/展开的内容 -->
              <div
                v-if="isReferenceOpen"
                class="grid md:grid-cols-3 gap-2 md:gap-4"
              >
                <!-- Première ligne: Catégories (3 niveaux) - 3 par ligne -->
                <div class="md:col-span-3">
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <!-- Catégorie niveau 1 -->
                    <div>
                      <label
                        class="block text-[11px] font-medium text-black mb-1"
                        >Catégorie</label
                      >
                      <select
                        v-model.number="(localProduct as any).catLevel1Id"
                        class="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      >
                        <option :value="null">
                          -- Choisir une catégorie --
                        </option>
                        <option
                          v-for="c in level1Categories"
                          :key="c.id_categorie"
                          :value="c.id_categorie"
                        >
                          {{ c.label }}
                        </option>
                      </select>
                    </div>

                    <!-- Catégorie niveau 2 -->
                    <div>
                      <label
                        class="block text-[11px] font-medium text-black mb-1"
                        >Sous-catégorie</label
                      >
                      <select
                        v-model.number="(localProduct as any).catLevel2Id"
                        :disabled="!level2Categories.length"
                        class="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-black"
                      >
                        <option :value="null">
                          -- Choisir une sous-catégorie --
                        </option>
                        <option
                          v-for="c in level2Categories"
                          :key="c.id_categorie"
                          :value="c.id_categorie"
                        >
                          {{ c.label }}
                        </option>
                      </select>
                    </div>

                    <!-- Catégorie niveau 3 -->
                    <div>
                      <label
                        class="block text-[11px] font-medium text-black mb-1"
                        >SS-catégorie</label
                      >
                      <select
                        v-model.number="(localProduct as any).catLevel3Id"
                        :disabled="!level3Categories.length"
                        class="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-black"
                      >
                        <option :value="null">
                          -- Choisir une ss-catégorie --
                        </option>
                        <option
                          v-for="c in level3Categories"
                          :key="c.id_categorie"
                          :value="c.id_categorie"
                        >
                          {{ c.label }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>

                <!-- Vendu par / Déclinaison / Pack -->
                <div class="md:col-span-3">
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <!-- Vendu par -->
                    <div>
                      <label
                        class="block text-[11px] font-medium text-black mb-1"
                      >
                        Vendu par
                        <span
                          v-if="isAdmin"
                          class="ml-1 text-[10px] text-orange-500 font-semibold"
                          >(admin)</span
                        >
                      </label>
                      <!-- Admin : select éditable -->
                      <select
                        v-if="isAdmin"
                        :value="(localProduct as any).venduPar"
                        @change="
                          onVenduParChange(
                            ($event.target as HTMLSelectElement).value,
                          )
                        "
                        class="w-full rounded-md border border-orange-400 px-3 py-1.5 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                      >
                        <option value="couleurs">À la couleur</option>
                        <option value="melangees">Couleurs mélangées</option>
                        <option value="tailles">Taille au choix</option>
                      </select>
                      <!-- Non-admin : lecture seule -->
                      <select
                        v-else
                        v-model="(localProduct as any).venduPar"
                        disabled
                        class="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm bg-gray-100 cursor-not-allowed"
                      >
                        <option value="couleurs">À la couleur</option>
                        <option value="melangees">Couleurs mélangées</option>
                        <option value="tailles">Taille au choix</option>
                      </select>
                    </div>

                    <!-- Modale de confirmation changement de type de vente (admin) -->
                    <Teleport to="body">
                      <div
                        v-if="showVenduParConfirm"
                        class="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50"
                      >
                        <div
                          class="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4"
                        >
                          <div class="flex items-center gap-3 mb-4">
                            <div
                              class="flex-shrink-0 w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center"
                            >
                              <i
                                class="fas fa-exclamation-triangle text-orange-500"
                              />
                            </div>
                            <div>
                              <h3 class="text-sm font-semibold text-gray-900">
                                Changement de type de vente
                              </h3>
                              <p class="text-xs text-gray-500">
                                Action réservée aux administrateurs
                              </p>
                            </div>
                          </div>
                          <p class="text-sm text-gray-700 mb-2">
                            Tu vas passer ce produit de
                            <strong>{{
                              venduParLabels[(localProduct as any)?.venduPar]
                            }}</strong>
                            à
                            <strong>{{
                              venduParLabels[pendingVenduPar!]
                            }}</strong
                            >.
                          </p>
                          <p
                            class="text-xs text-orange-600 bg-orange-50 rounded-md px-3 py-2 mb-5"
                          >
                            ⚠️ Les variantes et stocks incompatibles avec le
                            nouveau type seront supprimés lors de la sauvegarde.
                          </p>
                          <div class="flex justify-end gap-3">
                            <button
                              type="button"
                              @click="cancelVenduParChange"
                              class="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                            >
                              Annuler
                            </button>
                            <button
                              type="button"
                              @click="confirmVenduParChange"
                              class="px-4 py-2 text-sm rounded-lg bg-orange-500 text-white font-medium hover:bg-orange-600"
                            >
                              Confirmer le changement
                            </button>
                          </div>
                        </div>
                      </div>
                    </Teleport>

                    <!-- Déclinaison -->
                    <div>
                      <label
                        class="block text-[11px] font-medium text-black mb-1"
                        >Déclinaison</label
                      >
                      <select
                        v-model="(localProduct as any).id_declinaison"
                        @change="
                          if (!(localProduct as any).id_declinaison) {
                            (localProduct as any).id_pack = null;
                            (localProduct as any).packId = null;
                          }
                        "
                        class="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      >
                        <option :value="null">Choisir une déclinaison</option>
                        <option
                          v-for="d in declinaisons"
                          :key="d.id_declinaison"
                          :value="d.id_declinaison"
                        >
                          {{ formatDeclinaisonLabel(d.titre, d.tailles) }}
                        </option>
                      </select>
                    </div>

                    <!-- Pack -->
                    <div
                      v-if="(localProduct as any).venduPar !== 'tailles' && (localProduct as any).vendu_par !== 'tailles'"
                    >
                      <label
                        class="block text-[11px] font-medium text-black mb-1"
                        >Quantité du paquet</label
                      >
                      <select
                        v-model="(localProduct as any).id_pack"
                        class="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      >
                        <option :value="null">Choisir un pack</option>
                        <option
                          v-for="p in filteredPacks"
                          :key="p.id_pack"
                          :value="p.id_pack"
                        >
                          {{
                            p.valeur ? p.titre + " (" + p.valeur + ")" : p.titre
                          }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>

                <!-- Collection (déplacée sous les catégories) -->
                <div>
                  <label class="block text-[11px] font-medium text-black mb-1"
                    >Collection</label
                  >
                  <select
                    v-model.number="(localProduct as any).id_collection"
                    class="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  >
                    <option :value="null">-- Choisir une collection --</option>
                    <option
                      v-for="col in collections"
                      :key="col.id_collection"
                      :value="col.id_collection"
                    >
                      {{ col.libelle }}
                    </option>
                  </select>
                </div>

                <!-- Marque -->
                <div>
                  <label class="block text-[11px] font-medium text-black mb-1"
                    >Marque</label
                  >
                  <select
                    :value="(localProduct as any).marqueId ?? ''"
                    class="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    @change="onMarqueChange"
                  >
                    <option value="">-- Choisir une marque --</option>
                    <option
                      v-for="b in brands ?? []"
                      :key="b.id"
                      :value="b.number"
                    >
                      {{ b.name }}
                    </option>
                  </select>
                </div>

                <!-- Couleur -->
                <div>
                  <label class="block text-[11px] font-medium text-black mb-1"
                    >Couleur</label
                  >
                  <button
                    type="button"
                    class="w-full flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm bg-white hover:border-blue-400 hover:bg-blue-50 transition-colors text-left"
                    @click="emitEditColors"
                  >
                    <template v-if="sheetColorSwatches.length">
                      <span
                        v-for="(swatch, index) in sheetColorSwatches"
                        :key="`sheet-swatch-${index}`"
                        class="inline-block w-4 h-4 rounded-full flex-shrink-0 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.18)]"
                        :style="swatch.style"
                        :title="swatch.label"
                      ></span>
                      <span class="text-sm text-black truncate flex-1">
                        {{ sheetColorLabels }}
                      </span>
                    </template>
                    <span v-else class="text-sm text-gray-400 flex-1"
                      >Choisir une couleur</span
                    >
                    <i class="fas fa-pen text-[10px] text-blue-500 flex-shrink-0"></i>
                  </button>
                </div>

                <!-- Référence -->
                <div>
                  <label class="block text-[11px] font-medium text-black mb-1">
                    Référence de base *
                  </label>
                  <input
                    v-model="(localProduct as any).reference_base"
                    type="text"
                    class="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <!-- Prix -->
                <div>
                  <label class="block text-[11px] font-medium text-black mb-1">
                    Prix (€) *
                  </label>
                  <input
                    :value="localProduct.prix"
                    type="number"
                    step="0.10"
                    class="w-full rounded-md border border-gray-300 px-3 py-1.5 text-base sm:text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    @change="(e) => { const v = parseFloat((e.target as HTMLInputElement).value); if (!isNaN(v)) localProduct.prix = v; }"
                  />
                </div>

                <!-- Prix réduit -->
                <div>
                  <label class="block text-[11px] font-medium text-black mb-1">
                    Prix réduit
                  </label>
                  <input
                    v-model="prixReduitInput"
                    type="text"
                    inputmode="decimal"
                    autocomplete="off"
                    class="w-full rounded-md border border-gray-300 px-3 py-1.5 text-base sm:text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    @blur="formatPrixReduitField"
                  />
                </div>

                <!-- Poids -->
                <div>
                  <label class="block text-[11px] font-medium text-black mb-1">
                    Poids unitaire (kg)
                  </label>
                  <input
                    v-model.number="localProduct.poids"
                    type="number"
                    step="0.10"
                    min="0.001"
                    :max="MAX_MEL_UNIT_WEIGHT_KG"
                    class="w-full rounded-md border border-gray-300 px-3 py-1.5 text-base sm:text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    :class="{
                      'border-red-500 focus:border-red-500 focus:ring-red-500':
                        isUnitWeightOverMax,
                    }"
                  />
                  <p
                    v-if="isUnitWeightOverMax"
                    class="text-xs font-semibold text-red-600 mt-1"
                  >
                    {{ MAX_MEL_UNIT_WEIGHT_ERROR }}
                  </p>
                  <p
                    v-else-if="isUnitWeightOver2kg"
                    class="text-xs font-semibold text-orange-600 mt-1"
                  >
                    Le poids saisi semble élevé (> 2 kg). Vérifiez qu’il
                    correspond bien au produit.
                  </p>
                </div>

                <!-- Provenance -->
                <div>
                  <label class="block text-[11px] font-medium text-black mb-1">
                    Provenance
                  </label>

                  <select
                    v-model.number="(localProduct as any).id_provenance"
                    class="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  >
                    <option :value="null">Sélectionner une provenance</option>
                    <option
                      v-for="prov in props.provenances"
                      :key="prov.id_provenance"
                      :value="prov.id_provenance"
                    >
                      {{ prov.libelle }}
                    </option>
                  </select>
                </div>

                <div v-if="showProductDimensions">
                  <label class="block text-[11px] font-medium text-black mb-1">
                    {{
                      compositionType === "chaussures"
                        ? "Dimensions du colis (cm, facultatif)"
                        : "Dimensions"
                    }}
                  </label>
                  <input
                    v-model="(localProduct as any).dimensions"
                    type="text"
                    class="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>
            </section>

            <!-- ========== STOCK ========== -->
            <section
              ref="stockSectionRef"
              class="rounded-2xl border border-gray-200 bg-gray-50 p-4"
            >
              <div class="mb-3">
                <h3 class="text-sm font-semibold text-black">
                  {{ stockSectionTitle }}
                </h3>
                <p
                  v-if="venduParComputed === 'melangees'"
                  class="text-xs text-gray-500 mt-1"
                >
                  Pour chaque couleur, indiquez la composition du paquet par
                  taille. La somme doit correspondre à la quantité du paquet.
                </p>
                <p
                  v-else-if="venduParComputed === 'couleurs'"
                  class="text-xs text-gray-500 mt-1"
                >
                  Nombre de paquets disponibles par couleur (le contenu du
                  paquet est défini par « Quantité du paquet » ci-dessus).
                </p>
                <p
                  v-else-if="venduParComputed === 'tailles'"
                  class="text-xs text-gray-500 mt-1"
                >
                  Quantités disponibles par couleur et taille (pas de paquet
                  pour ce mode de vente).
                </p>
              </div>

              <div>
                <!-- Mode tailles: couleur × taille -->
                <div v-if="venduParComputed === 'tailles'">
                  <div v-if="localTailleStocks.length" class="space-y-2">
                    <div
                      v-for="c in localTailleStocks"
                      :key="c.id_couleur"
                      class="flex items-center gap-3 px-2 py-1 rounded-md bg-white"
                    >
                      <div class="flex items-center gap-2 flex-shrink-0 w-28">
                        <span
                          class="inline-block w-3 h-3 rounded-full flex-shrink-0"
                          :style="{ backgroundColor: c.hexColor || '#CCCCCC' }"
                        ></span>
                        <span class="text-xs font-medium text-black truncate">{{
                          c.colorLabel
                        }}</span>
                      </div>
                      <div class="flex flex-wrap gap-2">
                        <div
                          v-for="ts in c.tailleStocks"
                          :key="ts.taille"
                          class="flex items-center gap-1"
                        >
                          <span class="text-xs text-black">{{
                            ts.taille
                          }}</span>
                          <input
                            :value="
                              ts.quantity != null ? String(ts.quantity) : ''
                            "
                            type="number"
                            min="0"
                            class="w-16 px-2 py-1 text-base sm:text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-center"
                            @input="
                              onTailleStockQuantityInput(
                                ts,
                                ($event.target as HTMLInputElement).value,
                              )
                            "
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <p v-else class="text-xs text-black">
                    Aucun stock renseigné pour ce produit.
                  </p>
                </div>

                <!-- Mode melangees: stock du paquet + quantités par couleur × taille -->
                <div v-else-if="venduParComputed === 'melangees'">
                  <div
                    class="mb-3 flex items-center gap-3 px-2 py-2 rounded-md bg-white"
                  >
                    <span class="text-xs font-medium text-black w-28"
                      >Stock en paquet</span
                    >
                    <input
                      :value="
                        melangeesPackStock != null
                          ? String(melangeesPackStock)
                          : ''
                      "
                      type="number"
                      min="0"
                      class="w-16 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-center"
                      @input="
                        onMelangeesPackStockInput(
                          ($event.target as HTMLInputElement).value,
                        )
                      "
                    />
                  </div>
                  <div v-if="localMelangeesStocks.length" class="space-y-2">
                    <div
                      v-for="row in localMelangeesStocks"
                      :key="row.id_couleur"
                      class="flex items-center gap-3 px-2 py-1 rounded-md bg-white"
                    >
                      <!-- Couleur -->
                      <div class="flex items-center gap-2 flex-shrink-0 w-28">
                        <span
                          class="inline-block w-3 h-3 rounded-full flex-shrink-0 border border-gray-300"
                          :style="{
                            backgroundColor: row.hexColor || '#CCCCCC',
                          }"
                        />
                        <span class="text-xs font-medium text-black truncate">{{
                          row.colorLabel
                        }}</span>
                      </div>
                      <!-- Quantités par taille -->
                      <div class="flex flex-wrap gap-2">
                        <div
                          v-for="qs in row.quantities"
                          :key="qs.taille"
                          class="flex items-center gap-1"
                        >
                          <span class="text-xs text-gray-500">{{
                            qs.taille
                          }}</span>
                          <input
                            :value="
                              qs.quantity != null ? String(qs.quantity) : ''
                            "
                            type="number"
                            min="0"
                            class="w-14 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-center"
                            @input="
                              qs.quantity =
                                ($event.target as HTMLInputElement).value !== ''
                                  ? Number(
                                      ($event.target as HTMLInputElement).value,
                                    )
                                  : null;
                              stockModified = true;
                            "
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <p v-else class="text-xs text-black">
                    Aucune couleur renseignée pour ce produit mélangé.
                  </p>
                </div>

                <!-- Mode couleurs: stock par couleur -->
                <div v-else>
                  <div v-if="localStocks.length" class="space-y-2">
                    <div
                      v-for="(s, index) in localStocks"
                      :key="'card-' + index"
                      class="flex items-center gap-2 md:gap-3 text-sm cursor-pointer flex-wrap"
                      @click="activeStockIndex = index"
                    >
                      <div class="flex items-center gap-2 min-w-[90px]">
                        <span
                          class="w-3 h-3 rounded-full inline-block border border-gray-300"
                          :style="
                            s.hexColor ? { backgroundColor: s.hexColor } : {}
                          "
                        />
                        <span
                          class="uppercase tracking-wide text-xs text-black"
                        >
                          {{ s.colorLabel }}
                        </span>
                      </div>
                      <input
                        :value="s.quantity != null ? String(s.quantity) : ''"
                        type="text"
                        inputmode="numeric"
                        pattern="[0-9]*"
                        class="w-16 rounded-md border border-gray-300 px-2 py-1 text-xs text-center focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        @input="
                          onStockQuantityInput(
                            s,
                            ($event.target as HTMLInputElement).value,
                          )
                        "
                      />
                      <div class="flex flex-col gap-1">
                        <button
                          type="button"
                          class="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors"
                          title="Mettre en rupture (stock à 0)"
                          @click="setStockRupture(s)"
                        >
                          <i class="fas fa-ban text-[10px]" />
                          <span>Rupture</span>
                        </button>
                        <button
                          type="button"
                          class="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 transition-colors"
                          title="Marquer disponible (stock à 1)"
                          @click="setStockDisponibleQuantity(s)"
                        >
                          <i class="fas fa-check text-[10px]" />
                          <span>Disponible</span>
                        </button>
                      </div>
                    </div>
                  </div>
                  <p v-else class="text-xs text-black">
                    Aucun stock renseigné pour ce produit.
                  </p>
                </div>
              </div>
            </section>

            <!-- ========== COMPOSITIONS + CARACTÉRISTIQUES ========== -->
            <div class="grid gap-6 md:grid-cols-2">
              <!-- Compositions - Unified with ListMelView using CompositionForm -->
              <section class="rounded-xl border border-gray-200 bg-white p-4">
                <CompositionForm
                  v-model="compositionFormData"
                  :compositionType="compositionType"
                  :localisations="compositionLocalisations"
                  :compositionsList="compositionsList"
                  :autocompleteOptions="compositionAutocompleteOptions"
                  :showAutocompleteDropdown="showCompositionDropdown"
                  :isLoadingAutocomplete="isLoadingCompositionAutocomplete"
                  @composition-input="handleCompositionInput"
                  @hide-dropdown="hideCompositionDropdown"
                  @select-autocomplete="selectComposition"
                />
              </section>

              <!-- Caractéristiques  -->
              <section class="rounded-xl border border-gray-200 bg-white p-4">
                <div class="flex items-center justify-between mb-3">
                  <h3 class="text-sm font-semibold text-black">
                    Caractéristiques
                    <span
                      v-if="selectedCaracteristiques.length > 0"
                      class="text-xs font-normal text-black ml-2"
                    >
                      ({{ selectedCaracteristiques.length }}/5)
                    </span>
                  </h3>
                  <button
                    type="button"
                    class="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                    @click="isCaracteristiquesOpen = !isCaracteristiquesOpen"
                  >
                    <i
                      class="fas"
                      :class="
                        isCaracteristiquesOpen
                          ? 'fa-chevron-up'
                          : 'fa-chevron-down'
                      "
                    />
                    <span>{{
                      isCaracteristiquesOpen ? "Réduire" : "Afficher"
                    }}</span>
                  </button>
                </div>

                <div v-if="isCaracteristiquesOpen">
                  <div
                    v-if="isLoadingCaracteristiques"
                    class="text-xs text-black py-2"
                  >
                    <i class="fas fa-spinner fa-spin mr-1"></i> Chargement...
                  </div>
                  <div
                    v-else-if="allCaracteristiques.length === 0"
                    class="text-xs text-black py-2"
                  >
                    Aucune caractéristique disponible pour cette catégorie.
                  </div>
                  <div
                    v-else
                    class="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 text-xs"
                  >
                    <label
                      v-for="c in allCaracteristiques"
                      :key="c.id_caracteristique"
                      class="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        class="h-3 w-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        :checked="
                          isCaracteristiqueSelected(c.id_caracteristique)
                        "
                        :disabled="
                          !isCaracteristiqueSelected(c.id_caracteristique) &&
                          selectedCaracteristiques.length >= 5
                        "
                        @change="toggleCaracteristique(c.id_caracteristique)"
                      />
                      <span class="text-black">{{ c.libelle }}</span>
                    </label>
                  </div>
                  <p class="text-xs text-black mt-2">
                    Maximum 5 caractéristiques
                  </p>
                </div>
              </section>
            </div>

            <!-- Descriptions + AI Generate -->
            <section class="rounded-xl border border-gray-200 bg-white p-4">
              <div class="flex items-center justify-between mb-3">
                <h3 class="text-sm font-semibold text-black">
                  Descriptions ( Détection automatique )
                </h3>
                <button
                  type="button"
                  @click="autoTranslateDescription"
                  :disabled="isTranslatingDesc"
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <svg
                    v-if="isTranslatingDesc"
                    class="animate-spin h-3.5 w-3.5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    ></path>
                  </svg>
                  <span v-else>🤖</span>
                  {{ isTranslatingDesc ? "Translating..." : "AI Traduction" }}
                </button>
              </div>

              <div class="space-y-2">
                <!-- Single input box -->
                <textarea
                  v-model="descriptionInput"
                  class="w-full rounded-md border border-gray-300 px-3 py-2 text-xs focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  rows="4"
                  placeholder="Entrez la description, puis cliquez AI Traduction"
                ></textarea>

                <!-- Generated translations -->
                <div
                  v-if="
                    descriptionFr ||
                    descriptionEn ||
                    descriptionIt ||
                    descriptionEs ||
                    descriptionDe
                  "
                  class="grid grid-cols-1 md:grid-cols-2 gap-4"
                >
                  <div
                    v-for="lang in [
                      { label: 'Français', model: descriptionFr, key: 'fr' },
                      { label: 'English', model: descriptionEn, key: 'en' },
                      { label: 'Italiano', model: descriptionIt, key: 'it' },
                      { label: 'Español', model: descriptionEs, key: 'es' },
                      { label: 'Deutsch', model: descriptionDe, key: 'de' },
                    ]"
                    :key="lang.key"
                  >
                    <label class="block text-sm font-medium text-black mb-1">{{
                      lang.label
                    }}</label>
                    <textarea
                      :value="
                        lang.key === 'fr'
                          ? descriptionFr
                          : lang.key === 'en'
                          ? descriptionEn
                          : lang.key === 'it'
                          ? descriptionIt
                          : lang.key === 'es'
                          ? descriptionEs
                          : descriptionDe
                      "
                      @input="(e: Event) => { const v = (e.target as HTMLTextAreaElement).value; if (lang.key === 'fr') descriptionFr = v; else if (lang.key === 'en') descriptionEn = v; else if (lang.key === 'it') descriptionIt = v; else if (lang.key === 'es') descriptionEs = v; else descriptionDe = v; }"
                      class="w-full rounded-md border border-gray-300 px-3 py-2 bg-gray-50 text-xs text-black"
                      rows="3"
                    ></textarea>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <!-- Footer -->
          <footer
            class="flex-shrink-0 flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50"
          >
            <button
              type="button"
              class="rounded-md border border-gray-300 px-4 py-1.5 text-xs font-medium text-black hover:bg-white"
              @click="close"
            >
              Annuler
            </button>
            <button
              type="button"
              class="inline-flex items-center rounded-md bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              @click="handleSave"
            >
              <i class="fas fa-save mr-2 text-[11px]" />
              Enregistrer la fiche
            </button>
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- Image Lightbox Overlay -->
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="isLightboxOpen && currentImage"
        class="fixed inset-0 z-[10000] bg-black/80 flex items-center justify-center"
        @click.self="closeLightbox"
      >
        <!-- Close button -->
        <button
          type="button"
          class="absolute top-4 right-4 text-white/90 hover:text-white bg-white/10 hover:bg-white/20 rounded-full w-10 h-10 flex items-center justify-center"
          @click="closeLightbox"
          aria-label="Fermer l'aperçu"
        >
          <i class="fas fa-times text-lg"></i>
        </button>

        <!-- Navigation arrows in lightbox -->
        <button
          v-if="localImages.length > 1"
          type="button"
          class="absolute left-4 top-1/2 -translate-y-1/2 text-white bg-white/10 hover:bg-white/20 rounded-full w-12 h-12 flex items-center justify-center"
          @click.stop="prevImage"
          aria-label="Image précédente"
        >
          <i class="fas fa-chevron-left text-xl"></i>
        </button>
        <button
          v-if="localImages.length > 1"
          type="button"
          class="absolute right-4 top-1/2 -translate-y-1/2 text-white bg-white/10 hover:bg-white/20 rounded-full w-12 h-12 flex items-center justify-center"
          @click.stop="nextImage"
          aria-label="Image suivante"
        >
          <i class="fas fa-chevron-right text-xl"></i>
        </button>

        <!-- Image -->
        <img
          :src="getDisplayPhotoUrl(currentImage)"
          :alt="localProduct?.reference || 'Aperçu image'"
          class="max-w-[95vw] max-h-[90vh] object-contain select-none shadow-2xl rounded-2xl transition-transform duration-200"
          :style="{
            transform: `rotate(${getPendingRotationDegrees(currentImage)}deg)`,
          }"
          @click.stop
          @error="onImageError"
        />
      </div>
    </Transition>
  </Teleport>

  <!-- Modal confirmation suppression photo (comme gestion des photos avancée) -->
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="showDeletePhotoModal && photoPendingDeletion"
        class="fixed inset-0 z-[10001] flex items-center justify-center bg-black/40 p-4"
        @click.self="
          showDeletePhotoModal = false;
          photoPendingDeletion = null;
        "
      >
        <div
          class="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
          @click.stop
        >
          <h3 class="text-lg font-semibold text-black mb-2">
            Confirmer la suppression
          </h3>
          <p class="text-sm text-black mb-4">
            Êtes-vous sûr de vouloir supprimer cette photo ?
          </p>
          <div class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-4">
            <div
              class="w-16 h-16 bg-gray-200 rounded overflow-hidden flex-shrink-0"
            >
              <img
                :src="
                  photoPendingDeletion
                    ? getDisplayPhotoUrl(photoPendingDeletion)
                    : ''
                "
                alt="Photo à supprimer"
                class="w-full h-full object-cover"
              />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-black">
                {{ localProduct?.reference }}
              </p>
              <p class="text-xs text-black truncate">
                {{ getPhotoFilename(photoPendingDeletion) }}
              </p>
            </div>
          </div>
          <div class="flex justify-end gap-2">
            <button
              type="button"
              class="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-black hover:bg-gray-50"
              @click="
                showDeletePhotoModal = false;
                photoPendingDeletion = null;
              "
            >
              Annuler
            </button>
            <button
              type="button"
              class="px-3 py-1.5 text-sm rounded-md text-white bg-red-600 hover:bg-red-700 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              :disabled="isDeletingPhoto"
              @click="confirmDeletePhoto"
            >
              <i
                v-if="isDeletingPhoto"
                class="fas fa-spinner fa-spin text-sm"
              />
              Supprimer
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import {
  computed,
  ref,
  watch,
  onBeforeUnmount,
  onMounted,
  nextTick,
  toRefs,
} from "vue";
import type { ProduitCatalogue } from "@/types";
import {
  getCaracteristiques,
  getCompositions,
  getProduitCaracteristiqueIds,
  getProduitCompositions,
  getProduitCouleurs,
  getProduitDescription,
  getProduitStocks,
  saveProduitCompositions,
  saveProduitDescription,
  saveProduitStocks,
  upsertProduitStock,
  updateProduitCaracteristiques,
  updateProduitCouleursProduit,
  updateCouleurDescriptionPaquet,
} from "@/api/produits";
import { getCompositionFamilies } from "@/api/produits";
import { CompositionFamilleOption } from "@/types/compositionFamilleOption";
import { CompositionOption } from "@/types/compositionOption";
import { getReferenceData, getCompositionAutocomplete } from "@/api/shooting";
import { CompositionForm } from "@/components";
import { CaracteristiqueOption } from "@/types/caracteristiqueOption";
import { useNotification } from "@/composables/useNotification";
import frFlag from "@/assets/images/france.png";
import ukFlag from "@/assets/images/royaume-uni.png";
import { getColorSwatchStyle } from "@/utils/colorFamilies";
import {
  getProductPhotos,
  deleteProductPhoto,
  rotateProductPhoto,
  reorderProductPhotos,
  uploadProductPhotos,
  isSupportedProductPhotoFormat,
  getUnsupportedProductPhotoErrorMessage,
  productPhotoInputAccept,
} from "@/api/shooting";
import {
  fetchRest,
  getApiBaseUrl,
  getProductImageUrl,
  getProductOriginalImageUrl,
} from "@/config/api";
import { bumpPhotoVersion, getPhotoVersion } from "@/utils/photoVersion";
import {
  isShoeCategory,
  isValidProductDimensions,
} from "@/utils/productDimensions";
import {
  MAX_MEL_UNIT_WEIGHT_KG,
  MAX_MEL_UNIT_WEIGHT_ERROR,
  WARN_MEL_UNIT_WEIGHT_KG,
  getMelUnitWeightError,
} from "@/utils/melCompositions";
import { getCollections, type CollectionOption } from "@/api/collections";
import { getDeclinaisonById } from "@/api/gammesTailles";
// Packs & déclinaisons pour le vendeur
import {
  getPacksByVendeur,
  getDeclinaisonsByVendeur,
  type VendeurDeclinaison as ApiVendeurDeclinaison,
} from "@/api/produits";
import type { PackOption } from "@/types/packOption";
import { getPackLength } from "@/utils/packLength";
import {
  formatDescriptionPaquet,
  parseDescriptionPaquet,
} from "@/utils/descriptionPaquet";
import {
  formatDeclinaisonLabel,
  getDeclinaisonLength,
} from "@/utils/declinaisonLabel";
import { useAuthStore } from "@/stores/auth";

// Étendre le type pour inclure tailles (utilisé dans ProductListView)
interface VendeurDeclinaison extends ApiVendeurDeclinaison {
  tailles?: string;
}

interface ColorStockRow {
  id_produit_stock?: number;
  id_couleur: number;
  colorLabel: string;
  quantity: number | null; // null = rien affiché (stock non renseigné)
  statut: "en-vente" | "hors-ligne"; // STATUT: seulement 'en vente' et 'hors ligne' (rupture supprimé)
  visible: boolean;
  dotClass: string;
  hexColor?: string | null;
  imageUrl?: string;
}

interface CompositionLine {
  id: number;
  famille: string; // "Produit"
  familleId: number; // id_composition_localisation
  libelle: string; // "Cuir"
  pourcentage: number;
  id_composition?: number;
}

interface Props {
  visible: boolean;
  product: ProduitCatalogue | null;
  provenances: { id_provenance: number; label: string }[];
  // Optional props passed by parent (ProductListView) — declare to avoid extraneous-attrs warning
  brands?: any[];
  categoriesTree?: any[];
  packs: PackOption[];
  declinaisons: VendeurDeclinaison[];
}

const props = defineProps<Props>();
const { packs: propsPacks, declinaisons: propsDeclinaisons } = toRefs(props);

// Détection admin (impersonation EFP)
const authStore = useAuthStore();
const isAdmin = computed(() => authStore.isImpersonating);

// État de la confirmation de changement de type de vente
const pendingVenduPar = ref<string | null>(null);
const showVenduParConfirm = ref(false);

function onVenduParChange(newVal: string) {
  if (!localProduct.value) return;
  const oldVal = (localProduct.value as any).venduPar;
  if (newVal === oldVal) return;
  pendingVenduPar.value = newVal;
  showVenduParConfirm.value = true;
}

function confirmVenduParChange() {
  if (!localProduct.value || !pendingVenduPar.value) return;
  const lp = localProduct.value as any;
  lp.venduPar = pendingVenduPar.value;
  lp.vendu_par = pendingVenduPar.value;
  if (pendingVenduPar.value === "tailles") {
    lp.id_pack = null;
    lp.packId = null;
  }
  showVenduParConfirm.value = false;
  pendingVenduPar.value = null;
}

function cancelVenduParChange() {
  pendingVenduPar.value = null;
  showVenduParConfirm.value = false;
}

function onMarqueChange(event: Event) {
  if (!localProduct.value) return;
  const raw = (event.target as HTMLSelectElement).value;
  const idMarque = raw === "" ? null : Number(raw);
  const lp = localProduct.value as any;
  lp.marqueId = idMarque;
  lp.id_vendeur_marque = idMarque;
  const brand = (props.brands ?? []).find((b: any) => b.number === idMarque);
  lp.marque = brand?.name ?? "";
}

const venduParLabels: Record<string, string> = {
  couleurs: "À la couleur",
  melangees: "Couleurs mélangées",
  tailles: "Taille au choix",
};

// Téléchargement autorisé pour tous les types de prestation
const canDownloadPhotos = computed((): boolean => {
  const p = localProduct.value;
  if (!p) return false;
  return true;
});

// Refs locales pour le fallback si les props sont vides
const localPacks = ref<PackOption[]>([]);
const localDeclinaisons = ref<VendeurDeclinaison[]>([]);

// Utiliser les props s'ils sont disponibles, sinon les refs locales
const packs = computed(() => {
  return propsPacks.value && propsPacks.value.length > 0
    ? propsPacks.value
    : localPacks.value;
});
const declinaisons = computed(() => {
  return propsDeclinaisons.value && propsDeclinaisons.value.length > 0
    ? propsDeclinaisons.value
    : localDeclinaisons.value;
});

const emit = defineEmits<{
  (e: "update:visible", value: boolean): void;
  (e: "save", payload: ProduitCatalogue & any): void;
  (
    e: "pending-cover",
    payload: { productId: number; coverUrl: string; nbPhotos?: number },
  ): void;
  (e: "edit-colors", product: ProduitCatalogue): void;
  (e: "stocks-updated", payload: {
    id_produit: number;
    stock_value: number;
    stock_renseigne: boolean;
    stocks: Array<{
      id_couleur: number;
      colorLabel: string;
      quantity: number | null;
      hexColor?: string | null;
    }>;
  }): void;
}>();

const localProduct = ref<ProduitCatalogue | null>(null);
const localPromotion = ref<number | null>(null);
// Texte séparé du nombre : v-model.number réécrit 0 dès qu'on le supprime.
const prixReduitInput = ref("");
const openedPrixReduit = ref<number | null>(null);

const readFinitePrice = (value: unknown): number | null => {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
};

const parsePrixReduitInput = (raw: string): number | null | undefined => {
  const normalized = raw.trim().replace(/,/g, ".").replace(/\.$/, "");
  if (normalized === "") return null;
  if (!/^\d+(\.\d+)?$/.test(normalized)) return undefined;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : undefined;
};

const syncPrixReduitInput = (value: unknown) => {
  const price = readFinitePrice(value);
  openedPrixReduit.value = price;
  prixReduitInput.value = price != null && price > 0 ? price.toFixed(2) : "";
};

const formatPrixReduitField = () => {
  const parsed = parsePrixReduitInput(prixReduitInput.value);
  if (typeof parsed !== "number" || parsed <= 0) return;
  prixReduitInput.value = parsed.toFixed(2);
};

const sheetColorSwatches = computed(() => {
  const p = localProduct.value;
  if (!p) return [] as { label: string; style: Record<string, string> }[];
  const details = p.couleursDetail;
  if (details?.length) {
    return details.map((c) => ({
      label: c.couleur_FR || `#${c.id_couleur}`,
      style: getColorSwatchStyle(c.couleur_FR || "", c.hexColor ?? null),
    }));
  }
  const raw = p.couleur;
  if (!raw || typeof raw !== "string") return [];
  return raw
    .split(",")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((label) => ({
      label,
      style: getColorSwatchStyle(label, null),
    }));
});

const sheetColorLabels = computed(() =>
  sheetColorSwatches.value.map((s) => s.label).join(", "),
);

const emitEditColors = () => {
  if (!localProduct.value) return;
  emit("edit-colors", localProduct.value);
};

watch(
  () =>
    props.product
      ? {
          id: props.product.id,
          couleur: props.product.couleur,
          reference: props.product.reference,
          colorIds: (props.product.colorIds || []).join(","),
          detailKey: (props.product.couleursDetail || [])
            .map((c) => `${c.id_couleur}:${c.couleur_FR}`)
            .join("|"),
        }
      : null,
  () => {
    const p = props.product;
    const lp = localProduct.value;
    if (!p || !lp || Number(lp.id) !== Number(p.id)) return;
    lp.couleur = p.couleur;
    lp.reference = p.reference;
    lp.colorIds = p.colorIds ? [...p.colorIds] : [];
    lp.couleursDetail = p.couleursDetail ? [...p.couleursDetail] : [];
  },
);

const localImages = ref<string[]>([]);
const activeImageIndex = ref(0);
const isLoadingImages = ref(false);

// Mobile photo overlay state (like UploadPhotoView)
const activePhotoOverlayIndex = ref<number | null>(null);

// Drag-and-drop state for photo reordering (cover photo at index 0 is fixed)
const draggingPhotoIndex = ref<number | null>(null);
const dragOverPhotoIndex = ref<number | null>(null);

const isPhotoOverlayActive = (index: number): boolean => {
  return activePhotoOverlayIndex.value === index;
};

const handleThumbDragStart = (event: DragEvent, index: number) => {
  draggingPhotoIndex.value = index;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(index));
  }
};

const handleThumbDragEnd = () => {
  draggingPhotoIndex.value = null;
  dragOverPhotoIndex.value = null;
};

const handleThumbDragOver = (event: DragEvent, index: number) => {
  if (draggingPhotoIndex.value === null) return;
  event.preventDefault();
  event.stopPropagation();
  dragOverPhotoIndex.value = index;
  if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
};

const handleThumbDragLeave = () => {
  // handled on drop
};

const handleThumbDrop = async (event: DragEvent, dropIndex: number) => {
  event.preventDefault();
  event.stopPropagation();
  const dragIndex = draggingPhotoIndex.value;
  draggingPhotoIndex.value = null;
  dragOverPhotoIndex.value = null;
  if (dragIndex === null || dragIndex === dropIndex) return;
  const pid = productIdForPhotos.value;
  if (pid == null) return;
  // Reorder localImages: move dragIndex to dropIndex
  const arr = [...localImages.value];
  const [moved] = arr.splice(dragIndex, 1);
  arr.splice(dropIndex, 0, moved);
  const previousOrder = [...localImages.value];
  localImages.value = arr;
  if (activeImageIndex.value === dragIndex) {
    activeImageIndex.value = dropIndex;
  } else if (
    dragIndex < activeImageIndex.value &&
    dropIndex >= activeImageIndex.value
  ) {
    activeImageIndex.value -= 1;
  } else if (
    dragIndex > activeImageIndex.value &&
    dropIndex <= activeImageIndex.value
  ) {
    activeImageIndex.value += 1;
  }
  // Build positions in new order for API
  const positionsInNewOrder = arr.map(getPhotoPositionFromUrl);
  try {
    await reorderProductPhotos(pid, positionsInNewOrder);
    setPhotoCacheBuster(pid);
    await loadUploadPhotos(pid);
  } catch (e) {
    localImages.value = previousOrder;
    notifyError(
      e instanceof Error
        ? e.message
        : "Erreur lors du réordonnancement des photos",
    );
  }
};

const togglePhotoOverlay = (index: number) => {
  // Mobile only: on desktop keep hover behavior (sm and above)
  if (typeof window !== "undefined" && window.matchMedia) {
    const isDesktop = window.matchMedia("(min-width: 640px)").matches;
    if (isDesktop) {
      return;
    }
  }

  activePhotoOverlayIndex.value = isPhotoOverlayActive(index) ? null : index;
};

const ID_COULEUR_MELANGEES = 43;

const localStocks = ref<ColorStockRow[]>([]);
const stockModified = ref(false);
const melangeesPackStock = ref<number | null>(null);
const melangeesPackStockModified = ref(false);
const initialVenduPar = ref<string | null>(null);
const activeStockIndex = ref(0);
const stockSectionRef = ref<HTMLElement | null>(null);
// Mode tailles: stock par couleur × taille
const localTailleStocks = ref<
  Array<{
    id_couleur: number;
    colorLabel: string;
    hexColor?: string;
    tailleStocks: Array<{ taille: string; quantity: number | null }>;
  }>
>([]);

// Mode mélangées: quantités par couleur × taille (description_paquet)
const localMelangeesStocks = ref<
  Array<{
    id_couleur: number;
    colorLabel: string;
    hexColor?: string;
    quantities: Array<{ taille: string; quantity: number | null }>;
  }>
>([]);
const venduParComputed = computed(() => {
  // Priorité à localProduct pour réagir en direct au changement de type de vente
  const local = (localProduct.value || {}) as any;
  if (local.venduPar) return local.venduPar;
  if (local.vendu_par) return local.vendu_par;
  const anyP = (props.product || {}) as any;
  return anyP.venduPar ?? anyP.vendu_par ?? "";
});

const stockSectionTitle = computed(() => {
  const v = venduParComputed.value;
  if (v === "tailles") return "Stock par taille";
  if (v === "melangees") return "Composition du paquet par couleur";
  if (v === "couleurs") return "Stock par couleur";
  return "Stock";
});

const notification = useNotification();

const notifyError = (message: string, duration = 5000) => {
  notification.error(message, duration);
};

const unitWeightValue = computed(() => Number(localProduct.value?.poids));
const isUnitWeightOverMax = computed(
  () =>
    Number.isFinite(unitWeightValue.value) &&
    unitWeightValue.value > MAX_MEL_UNIT_WEIGHT_KG,
);
const isUnitWeightOver2kg = computed(
  () =>
    Number.isFinite(unitWeightValue.value) &&
    unitWeightValue.value > WARN_MEL_UNIT_WEIGHT_KG &&
    unitWeightValue.value <= MAX_MEL_UNIT_WEIGHT_KG,
);

// Composition state - unified with ListMelView using CompositionForm
interface CompositionFormItem {
  localisationId: number;
  compositionId: number;
  compositionName: string;
  percentage: number | "";
}
const compositionFormData = ref<CompositionFormItem[]>([]);
const compositionType = ref<"autres" | "chaussures">("autres");
const showProductDimensions = computed(() => {
  const product = localProduct.value as any;
  const category =
    product?.catLevel3Id ?? product?.catLevel2Id ?? product?.catLevel1Id;
  return (
    compositionType.value === "chaussures" ||
    String(category ?? "").startsWith("15")
  );
});
const compositionLocalisations = ref<
  Array<{ id_composition_localisation: number; libelle: string }>
>([]);
const compositionsList = ref<
  Array<{ id_composition: number; libelle: string }>
>([]);
const compositionAutocompleteOptions = ref<
  Array<{ id: number; label: string }>
>([]);
const showCompositionDropdown = ref(false);
const isLoadingCompositionAutocomplete = ref(false);
const allCompositionsData = ref<
  Array<{
    id: number;
    libelle: string;
    id_categorie: string | null;
    type: "localisation" | "composition";
  }>
>([]);

// Legacy composition state (kept for backward compatibility during transition)
const compositions = ref<CompositionLine[]>([]);
const compositionFamilies = ref<CompositionFamilleOption[]>([]);
const compositionOptions = ref<CompositionOption[]>([]);
const newCompositionId = ref<number | 0>(0);

const isTopOpen = ref(true);
const isReferenceOpen = ref(true);
const isCaracteristiquesOpen = ref(true);
const isLoadingCaracteristiques = ref(false);
const newCompositionFamilleId = ref<number | null>(null);

// Lightbox state
const isLightboxOpen = ref(false);
const openLightbox = () => {
  if (!currentImage.value) return;
  isLightboxOpen.value = true;
};
const closeLightbox = () => {
  isLightboxOpen.value = false;
};

// Keyboard controls for lightbox
const onKeydown = (e: KeyboardEvent) => {
  if (!isLightboxOpen.value) return;
  if (e.key === "Escape") {
    e.preventDefault();
    closeLightbox();
  } else if (e.key === "ArrowRight") {
    e.preventDefault();
    nextImage();
  } else if (e.key === "ArrowLeft") {
    e.preventDefault();
    prevImage();
  }
};

// Fonction pour filtrer les packs selon la déclinaison (identique à ProductListView)
const getPacksForProduct = (product: ProduitCatalogue | null) => {
  if (!product || !(product as any).declinaisonId) {
    return packs.value;
  }

  const decl = declinaisons.value.find(
    (d) => d.id_declinaison === (product as any).declinaisonId,
  );
  if (!decl || !decl.tailles) {
    return packs.value;
  }

  const tailleCount = decl.tailles
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean).length;

  return packs.value.filter((p) => {
    return getPackLength(p.valeur) === tailleCount;
  });
};

// Réinitialiser le pack quand la déclinaison change
const onDeclinaisonChange = () => {
  if (localProduct.value) {
    // Vérifier si le pack actuel est toujours valide avec la nouvelle déclinaison
    const availablePacks = getPacksForProduct(localProduct.value);
    const currentPackId = (localProduct.value as any).packId;
    const isPackStillValid = availablePacks.some(
      (p) => p.id_pack === currentPackId,
    );

    if (!isPackStillValid && currentPackId) {
      // Réinitialiser le pack si il n'est plus compatible
      (localProduct.value as any).packId = null;
    }
  }
};

// Verrouillage du scroll de la page derrière la modal
const disableBodyScroll = () => {
  document.body.style.overflow = "hidden";
  document.body.style.paddingRight = "0px";
};
const enableBodyScroll = () => {
  document.body.style.overflow = "";
  document.body.style.paddingRight = "";
};

onMounted(() => {
  window.addEventListener("keydown", onKeydown);
});
onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeydown);
  enableBodyScroll();
});

// Close lightbox if parent modal closes and ensure categories cascade is initialized on open
watch(
  () => props.visible,
  async (v) => {
    if (!v) {
      isLightboxOpen.value = false;
      enableBodyScroll();
      return;
    }
    disableBodyScroll();

    // Charger les données quand le modal s'ouvre (une seule fois)
    await loadInitialData();

    // When opening the fiche, pre-fill Catégorie → Sous-catégorie → SS-catégorie
    // and Vendu par / Déclinaison / Pack after the DOM and option lists have a tick to render.
    try {
      await nextTick();
      if (typeof initLocalProductCategories === "function")
        initLocalProductCategories();
      if (typeof initLocalVDP === "function") initLocalVDP();
    } catch (e) {
      // Fail silently to avoid blocking modal open
    }
  },
);

// Caractéristiques
const allCaracteristiques = ref<CaracteristiqueOption[]>([]);
const selectedCaracteristiques = ref<number[]>([]);

// Descriptions
const descriptionFr = ref("");
const descriptionEn = ref("");
const descriptionIt = ref("");
const descriptionEs = ref("");
const descriptionDe = ref("");
const descriptionZh = ref("");
const descriptionInput = ref("");
const isTranslatingDesc = ref(false);

// Détecte la présence d'emojis / pictogrammes dans un texte
const EMOJI_REGEX =
  /([\u{1F000}-\u{1FAFF}]|[\u{2600}-\u{27BF}]|[\u{2300}-\u{23FF}]|[\u{2B00}-\u{2BFF}]|\u200D|\uFE0F)/u;

const containsEmoji = (text?: string | null): boolean =>
  !!text && EMOJI_REGEX.test(text);

const ALL_DESC_LANGS = [
  { key: "descriptionFr", lang: "francais", ref: () => descriptionFr },
  { key: "descriptionEn", lang: "anglais", ref: () => descriptionEn },
  { key: "descriptionIt", lang: "italien", ref: () => descriptionIt },
  { key: "descriptionEs", lang: "espagnol", ref: () => descriptionEs },
  { key: "descriptionDe", lang: "allemand", ref: () => descriptionDe },
];

async function autoTranslateDescription() {
  const text = descriptionInput.value?.trim();
  if (!text) {
    alert("Veuillez saisir une description.");
    return;
  }
  if (containsEmoji(text)) {
    notifyError(
      "La description ne doit pas contenir d'emojis : ils ne s'affichent pas correctement sur la fiche produit et perturbent la traduction automatique.",
      8000,
    );
    return;
  }
  isTranslatingDesc.value = true;
  try {
    const API_URL = getApiBaseUrl();
    const detectRes = await fetchRest(`${API_URL}/translate/detect`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text }),
    });
    const detectData = await detectRes.json();
    const detectedLang = detectData.language || "francais";

    const targetLangs = ALL_DESC_LANGS.filter(
      (e) => e.lang !== detectedLang,
    ).map((e) => e.lang);

    const response = await fetchRest(`${API_URL}/translate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text, sourceLang: detectedLang, targetLangs }),
    });
    if (!response.ok) throw new Error("Translation failed");
    const data = await response.json();

    for (const entry of ALL_DESC_LANGS) {
      if (entry.lang === detectedLang) {
        entry.ref().value = text;
      } else if (data[entry.lang]) {
        entry.ref().value = data[entry.lang];
      }
    }
  } catch (err) {
    console.error("Translation error:", err);
    alert("Erreur lors de la traduction. Veuillez réessayer.");
  } finally {
    isTranslatingDesc.value = false;
  }
}

const loadDescriptionsForProduct = async (id_produit: number) => {
  try {
    let desc = await getProduitDescription(id_produit);
    // Si le produit n'a pas de description et qu'il est une variante,
    // utiliser la description du produit principal (id_couleur_liee)
    if ((!desc || (!desc.texte_fr && !desc.texte_uk)) && props.product) {
      const anyP = props.product as any;
      const idCouleurLiee = anyP.id_couleur_liee;
      if (idCouleurLiee && Number(idCouleurLiee) !== id_produit) {
        const mainDesc = await getProduitDescription(Number(idCouleurLiee));
        if (mainDesc) desc = mainDesc;
      }
    }
    const brToNl = (s?: string) => (s ?? "").replace(/<br\s*\/?>/gi, "\n");
    descriptionInput.value = "";
    descriptionFr.value = brToNl(desc?.texte_fr);
    descriptionEn.value = brToNl(desc?.texte_uk);
    descriptionIt.value = brToNl(desc?.texte_it);
    descriptionEs.value = brToNl(desc?.texte_es);
    descriptionDe.value = brToNl(desc?.texte_de);
    descriptionZh.value = brToNl(desc?.texte_zh);
  } catch (e) {
    descriptionInput.value = "";
    descriptionFr.value = "";
    descriptionEn.value = "";
    descriptionIt.value = "";
    descriptionEs.value = "";
    descriptionDe.value = "";
    descriptionZh.value = "";
  }
};

const loadCaracteristiquesForProduct = async (id_produit: number) => {
  try {
    const ids = await getProduitCaracteristiqueIds(id_produit);
    selectedCaracteristiques.value = ids;
  } catch (e) {
    selectedCaracteristiques.value = [];
  }
};

// Load caracteristiques dynamically based on category (like ListMelView)
const loadCaracteristiquesByCategory = async (
  idCategorie: number | null | undefined,
) => {
  if (!idCategorie) {
    allCaracteristiques.value = [];
    return;
  }

  isLoadingCaracteristiques.value = true;
  try {
    const caracs = await getCaracteristiques("fr", idCategorie);
    allCaracteristiques.value = caracs;
  } catch (e) {
    allCaracteristiques.value = [];
  } finally {
    isLoadingCaracteristiques.value = false;
  }
};
const loadCompositionsForProduct = async (id_produit: number) => {
  try {
    // Ensure reference data is loaded before initializing compositions
    await loadInitialData();

    const rows = await getProduitCompositions(id_produit, "fr");
    // Legacy format
    compositions.value = rows.map((r, index) => ({
      id: index + 1,
      famille: r.famille,
      familleId: r.id_composition_localisation,
      libelle: r.libelle,
      pourcentage: r.value,
      id_composition: r.id_composition,
    }));

    // New CompositionForm format (unified with ListMelView)
    compositionFormData.value = rows.map((r: any) => ({
      localisationId: r.id_composition_localisation || 0,
      compositionId: r.id_composition,
      compositionName: r.libelle || "",
      percentage: r.value ? Number(r.value) : "",
    }));

    // Initialize composition type and lists
    await initCompositionData();
  } catch (e) {
    compositions.value = [];
    compositionFormData.value = [];
  }
};

const onMelangeesPackStockInput = (rawValue: string) => {
  const trimmed = rawValue.trim();
  if (trimmed === "") {
    melangeesPackStock.value = null;
  } else {
    const parsed = Number(trimmed);
    melangeesPackStock.value =
      Number.isFinite(parsed) && parsed >= 0 ? Math.trunc(parsed) : null;
  }
  melangeesPackStockModified.value = true;
};

const loadStocksForProduct = async (id_produit: number) => {
  stockModified.value = false;
  melangeesPackStock.value = null;
  melangeesPackStockModified.value = false;
  try {
    // Priorité à localProduct pour réagir au changement de type en cours d'édition
    const anyLocal = (localProduct.value || {}) as any;
    const anyP = (props.product || {}) as any;
    const venduPar =
      anyLocal.venduPar ??
      anyLocal.vendu_par ??
      anyP.venduPar ??
      anyP.vendu_par;
    const externalStockValue = anyP.stock_value;

    if (venduPar === "melangees") {
      // Mode melangees: quantités par couleur × taille via description_paquet
      localTailleStocks.value = [];
      localStocks.value = [];
      localMelangeesStocks.value = [];

      const [produitCouleurs, stockRows] = await Promise.all([
        getProduitCouleurs(id_produit),
        getProduitStocks(id_produit),
      ]);
      const mixedStock = stockRows.find(
        (row) =>
          Number(row.id_couleur) === ID_COULEUR_MELANGEES && !row.taille,
      );
      if (mixedStock?.value != null) {
        melangeesPackStock.value = Number(mixedStock.value);
      } else if (anyP.stock_renseigne && externalStockValue != null) {
        const fallback = Number(externalStockValue);
        melangeesPackStock.value = Number.isFinite(fallback) ? fallback : null;
      }

      // Récupérer les tailles depuis la déclinaison du produit
      const declinaisonId =
        anyLocal.declinaisonId ??
        anyLocal.id_declinaison ??
        anyP.declinaisonId ??
        anyP.id_declinaison;

      // Chercher dans declinaisons chargées, ou fetch direct si pas encore dispo
      let decl = declinaisons.value.find(
        (d) => Number(d.id_declinaison) === Number(declinaisonId),
      );
      if (!decl && declinaisonId) {
        try {
          const fetched = await getDeclinaisonById(Number(declinaisonId));
          if (fetched) decl = fetched as any;
        } catch (_e) {
          // déclinaison non trouvée, on continue sans tailles
        }
      }

      // Construire la liste des tailles depuis d1_FR … d12_FR
      const taillesList: string[] = [];
      if (decl) {
        for (let i = 1; i <= 12; i++) {
          const t = (decl as any)[`d${i}_FR`];
          if (t && typeof t === "string" && t.trim())
            taillesList.push(t.trim());
        }
        // Fallback : si la déclinaison a un champ tailles pré-calculé (VendeurDeclinaison)
        if (taillesList.length === 0 && (decl as any).tailles) {
          String((decl as any).tailles)
            .split(",")
            .forEach((t: string) => {
              if (t.trim()) taillesList.push(t.trim());
            });
        }
      }

      localMelangeesStocks.value = produitCouleurs.map((pc) => {
        // description_paquet = "1-2-3" (ou legacy "1,2,3") → quantités par taille
        const parts = parseDescriptionPaquet(pc.description_paquet);

        const quantities = taillesList.map((taille, i) => ({
          taille,
          quantity: parts[i] != null ? parts[i] : null,
        }));

        return {
          id_couleur: pc.id_couleur ?? 0,
          colorLabel: String(pc.couleur?.couleur_FR || "Couleur").toUpperCase(),
          hexColor: pc.couleur?.hex ?? undefined,
          quantities,
        };
      });

      activeStockIndex.value = 0;
      return;
    }

    if (venduPar === "couleurs") {
      // Mode couleurs: stock par couleur depuis produits_stock
      localTailleStocks.value = [];
      const rows = await getProduitStocks(id_produit);
      const byColor = new Map<
        number,
        { label: string; hex: string | undefined; total: number | null }
      >();
      for (const row of rows) {
        const couleurKey = row.id_couleur ?? 0;
        const existing = byColor.get(couleurKey);
        const val = row.value != null ? Number(row.value) : null;
        if (existing) {
          if (val != null) existing.total = (existing.total ?? 0) + val;
        } else {
          byColor.set(couleurKey, {
            label: String(row.couleur_FR || "Couleur").toUpperCase(),
            hex: row.hexColor ?? undefined,
            total: val,
          });
        }
      }
      // Si pas de stock en base, charger les couleurs du produit
      if (byColor.size === 0) {
        const produitCouleurs = await getProduitCouleurs(id_produit);
        for (const pc of produitCouleurs) {
          const couleurKey = pc.id_couleur ?? 0;
          byColor.set(couleurKey, {
            label: String(pc.couleur?.couleur_FR || "Couleur").toUpperCase(),
            hex: undefined,
            total: null,
          });
        }
      }

      localStocks.value = Array.from(byColor.entries()).map(
        ([id_couleur, data]) =>
          ({
            id_produit_stock: undefined,
            id_couleur,
            colorLabel: data.label,
            quantity: data.total,
            statut: "en-vente",
            visible: true,
            dotClass: "",
            hexColor: data.hex,
          } as ColorStockRow),
      );
      activeStockIndex.value = 0;
      return;
    }

    if (venduPar === "tailles") {
      // Mode tailles: stock par couleur × taille
      localStocks.value = [];
      const rows = await getProduitStocks(id_produit);

      // Récupérer la déclinaison complète pour s'assurer que TOUTES les tailles s'affichent
      const declinaisonIdT =
        anyLocal.declinaisonId ??
        anyLocal.id_declinaison ??
        anyP.declinaisonId ??
        anyP.id_declinaison;
      let declT = declinaisons.value.find(
        (d) => Number(d.id_declinaison) === Number(declinaisonIdT),
      );
      if (!declT && declinaisonIdT) {
        try {
          const fetched = await getDeclinaisonById(Number(declinaisonIdT));
          if (fetched) declT = fetched as any;
        } catch (_e) {
          // déclinaison non trouvée, on continue sans tailles
        }
      }
      const taillesList: string[] = [];
      if (declT) {
        for (let i = 1; i <= 12; i++) {
          const t = (declT as any)[`d${i}_FR`];
          if (t && typeof t === "string" && t.trim())
            taillesList.push(t.trim());
        }
        if (taillesList.length === 0 && (declT as any).tailles) {
          String((declT as any).tailles)
            .split(",")
            .forEach((t: string) => {
              if (t.trim()) taillesList.push(t.trim());
            });
        }
      }

      const byColor = new Map<
        number,
        {
          label: string;
          hex: string | undefined;
          tailleMap: Map<string, number | null>;
        }
      >();

      // Initialiser avec les couleurs du produit et TOUTES les tailles de la déclinaison
      const produitCouleurs = await getProduitCouleurs(id_produit);
      for (const pc of produitCouleurs) {
        const couleurKey = pc.id_couleur ?? 0;
        const tailleMap = new Map<string, number | null>();
        for (const t of taillesList) {
          tailleMap.set(t, null);
        }
        byColor.set(couleurKey, {
          label: String(pc.couleur?.couleur_FR || "Couleur").toUpperCase(),
          hex: pc.couleur?.hex ?? undefined,
          tailleMap,
        });
      }

      // Remplir avec les données de stock existantes en base
      for (const row of rows) {
        const couleurKey = row.id_couleur ?? 0;
        const taille = String(row.taille || "");
        const quantity = row.value != null ? Number(row.value) : null;

        if (!byColor.has(couleurKey)) {
          // Si une couleur a du stock mais n'est pas dans produitCouleurs (peu probable mais possible)
          const tailleMap = new Map<string, number | null>();
          for (const t of taillesList) {
            tailleMap.set(t, null);
          }
          byColor.set(couleurKey, {
            label: String(row.couleur_FR || "Couleur").toUpperCase(),
            hex: row.hexColor ?? undefined,
            tailleMap,
          });
        }

        const colorData = byColor.get(couleurKey)!;
        if (!taille || !taillesList.includes(taille)) {
          continue;
        }
        colorData.tailleMap.set(taille, quantity);
      }

      localTailleStocks.value = Array.from(byColor.entries()).map(
        ([id_couleur, data]) => ({
          id_couleur,
          colorLabel: data.label,
          hexColor: data.hex,
          tailleStocks: Array.from(data.tailleMap.entries()).map(
            ([taille, quantity]) => ({ taille, quantity }),
          ),
        }),
      );
      activeStockIndex.value = 0;
      await nextTick();
      stockSectionRef.value?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    // Fallback
    localTailleStocks.value = [];
    localStocks.value = [];
    activeStockIndex.value = 0;
  } catch (e) {
    localStocks.value = [];
    localTailleStocks.value = [];
  }
};

// Util: normalize URL by removing query parameters for deduplication
const normalizeUrlForDedup = (url: string): string => {
  if (!url) return "";
  return url.split("?")[0];
};

// Extract logical image key (filename) from URL: e.g. "123-c.jpg" or "123-z-1.jpg" / "123-z-1.png"
const getLogicalImageKey = (url: string): string => {
  if (!url) return "";
  const path = url.split("?")[0];
  const segment = path.split("/").pop() || "";
  const lower = segment.toLowerCase();
  return lower.endsWith(".jpg") || lower.endsWith(".png") ? segment : "";
};

// Photo principale = photo C (nom de fichier en -c.jpg / -c.png) — utilisé côté API
const isMainPhotoUrl = (url: string): boolean => {
  const key = getLogicalImageKey(url);
  return /-c\.(jpg|png)$/i.test(key);
};

// En affichage : la photo de couverture est simplement la première de la liste (index 0)
const isDisplayCover = (idx: number): boolean => idx === 0;

// Nom de fichier depuis l’URL (pour les API product-photo)
const getPhotoFilename = (photoUrl: string): string => {
  if (!photoUrl) return "";
  const path = photoUrl.split("?")[0];
  const segment = path.split("/").pop() || "";
  return segment;
};

// Position logique : c, z-1, z-2, …
const getPhotoPositionFromUrl = (photoUrl: string): string => {
  const key = getLogicalImageKey(photoUrl);
  if (!key) return "";
  const match = key.match(/^.+?-(c|z-\d+)\.(jpg|png)$/i);
  return match ? match[1] : "";
};

// URL pour ouvrir la photo en taille originale
const getOriginalPhotoUrl = (_productId: number, photoUrl: string): string =>
  getProductOriginalImageUrl(photoUrl);

// Sort keys: main (-c) first, then -z-1, -z-2, ... (jpg et png)
const sortLogicalKeys = (keys: string[]): string[] => {
  return [...keys].sort((a, b) => {
    const mainA = /-c\.(jpg|png)$/i.test(a);
    const mainB = /-c\.(jpg|png)$/i.test(b);
    if (mainA && !mainB) return -1;
    if (!mainA && mainB) return 1;
    if (mainA && mainB) return 0;
    const matchA = a.match(/-z-(\d+)\.(jpg|png)$/i);
    const matchB = b.match(/-z-(\d+)\.(jpg|png)$/i);
    const numA = matchA ? Number.parseInt(matchA[1], 10) : 0;
    const numB = matchB ? Number.parseInt(matchB[1], 10) : 0;
    return numA - numB;
  });
};

// En modal on affiche toujours en fiche (540×810). Convertir accueil → fiche pour les URLs venant de la liste.
const toFicheDisplayUrl = (url: string): string => {
  if (!url || typeof url !== "string") return url;
  return url
    .replace(/\/Produits\/accueil\//i, "/Produits/fiche/")
    .replace(/\/accueil\//i, "/fiche/");
};

// Deduplicate by logical image (filename). Same image in "accueil" (API) and "fiche" (legacy) = keep one.
// Prefer "fiche" (grande, 540×810) over "accueil" (250×375).
const deduplicateUrls = (urls: string[]): string[] => {
  const byKey = new Map<string, string>();
  for (const url of urls) {
    const pathKey = normalizeUrlForDedup(url);
    const logicalKey = getLogicalImageKey(url);
    // Si on a une clé logique (fichier produit type 123-c.jpg), dédupliquer par elle
    if (logicalKey) {
      const existing = byKey.get(logicalKey);
      const preferThis =
        !existing || (url.includes("/fiche/") && !existing.includes("/fiche/"));
      if (preferThis) byKey.set(logicalKey, url);
      continue;
    }
    // Sinon fallback: dédupliquer par path (comportement d’origine)
    if (pathKey && !byKey.has(pathKey)) byKey.set(pathKey, url);
  }
  const keys = sortLogicalKeys(Array.from(byKey.keys()));
  return keys.map((k) => byKey.get(k)!);
};

watch(
  () => props.product,
  async (p) => {
    if (!p) {
      localProduct.value = null;
      localPromotion.value = null;
      syncPrixReduitInput(null);
      localImages.value = [];
      localStocks.value = [];
      compositions.value = [];
      compositionFormData.value = [];
      selectedCaracteristiques.value = [];
      descriptionInput.value = "";
      descriptionFr.value = "";
      descriptionEn.value = "";
      return;
    }

    localProduct.value = { ...p };
    initialVenduPar.value =
      (p as any).venduPar ?? (p as any).vendu_par ?? null;
    // S'assurer que declinaisonId et packId sont bien mappés
    if ((p as any).id_declinaison && !(p as any).declinaisonId) {
      (localProduct.value as any).declinaisonId = (p as any).id_declinaison;
    }
    if ((p as any).id_pack && !(p as any).packId) {
      (localProduct.value as any).packId = (p as any).id_pack;
    }
    localPromotion.value = typeof p.promotion === "number" ? p.promotion : null;
    syncPrixReduitInput(p.prixReduit);

    const anyP = p as any;
    const idp = (anyP.id_produit as number | undefined) ?? (p as any).id;

    // Ne pas pré-remplir avec la photo de la liste (souvent une seule "c" ou legacy) :
    // on charge uniquement depuis l’API fiche (c + z-1, z-2, …). Fallback sur la liste seulement si l’API ne renvoie rien.
    const fallbackImgs: string[] = [];
    if (Array.isArray(anyP.images)) {
      anyP.images.forEach((src: string) => {
        const s = typeof src === "string" ? src.trim() : "";
        if (s) fallbackImgs.push(toFicheDisplayUrl(s));
      });
    }
    if (typeof anyP.image === "string" && anyP.image) {
      fallbackImgs.unshift(toFicheDisplayUrl(anyP.image));
    }

    localImages.value = [];
    activeImageIndex.value = 0;

    // Load caracteristiques based on product's category (like ListMelView)
    const idCategorie =
      anyP.id_categorie ||
      anyP.catLevel3Id ||
      anyP.catLevel2Id ||
      anyP.catLevel1Id;
    if (idCategorie) {
      await loadCaracteristiquesByCategory(idCategorie);
    } else {
      allCaracteristiques.value = [];
    }

    if (idp) {
      isLoadingImages.value = true;
      try {
        await Promise.all([
          loadStocksForProduct(idp),
          loadCompositionsForProduct(idp),
          loadCaracteristiquesForProduct(idp),
          loadDescriptionsForProduct(idp),
          loadUploadPhotos(idp),
        ]);
        // Si l’API n’a rien renvoyé (erreur ou mauvais serveur), afficher au moins la photo de la liste
        if (localImages.value.length === 0 && fallbackImgs.length > 0) {
          localImages.value = Array.from(new Set(fallbackImgs.filter(Boolean)));
          activeImageIndex.value = 0;
        }
      } finally {
        isLoadingImages.value = false;
      }
    } else {
      localImages.value = Array.from(new Set(fallbackImgs.filter(Boolean)));
      localStocks.value = [];
      compositions.value = [];
      compositionFormData.value = [];
      selectedCaracteristiques.value = [];
      descriptionInput.value = "";
      descriptionFr.value = "";
      descriptionEn.value = "";
    }

    if (!compositions.value.length && Array.isArray(anyP.compositions)) {
      const rawCompo = anyP.compositions as any[];
      compositions.value = rawCompo.map((c, index) => ({
        id: c.id ?? index + 1,
        famille: c.famille ?? c.kind ?? "Produit",
        libelle: c.libelle ?? c.label ?? "",
        pourcentage:
          typeof c.pourcentage === "number"
            ? c.pourcentage
            : typeof c.value === "number"
            ? c.value
            : 0,
        id_composition: c.id_composition ?? undefined,
      }));
    }
  },
  { immediate: true, deep: true },
);

// Fonction pour gérer le changement de quantité manuel
const handleQuantityChange = (stock: ColorStockRow) => {
  // Stock = 0 peut aussi être en vente, pas de changement automatique de statut
};

/** Input stock : vide → null (non renseigné), sinon nombre */
const onStockQuantityInput = (stock: ColorStockRow, rawValue: string) => {
  const trimmed = rawValue.trim();
  stock.quantity =
    trimmed === "" ? null : Math.max(0, parseInt(trimmed, 10) || 0);
  stockModified.value = true;
  handleQuantityChange(stock);
};

/** Input stock pour mode tailles */
const onTailleStockQuantityInput = (
  ts: { quantity: number | null },
  rawValue: string,
) => {
  const trimmed = rawValue.trim();
  ts.quantity = trimmed === "" ? null : Math.max(0, parseInt(trimmed, 10) || 0);
  stockModified.value = true;
};

// Fonction pour mettre le stock en "en-vente" (stock = 0 autorisé)
const setStockDisponible = (stock: ColorStockRow) => {
  stock.statut = "en-vente";
};

/** Met la ligne de stock en rupture (quantité à 0) */
const setStockRupture = (stock: ColorStockRow) => {
  stock.quantity = 0;
  stockModified.value = true;
};

/** Marque la ligne de stock comme disponible (quantité à 1) */
const setStockDisponibleQuantity = (stock: ColorStockRow) => {
  stock.quantity = 1;
  stockModified.value = true;
};

const currentImage = computed(() => {
  const stock = localStocks.value[activeStockIndex.value];
  if (stock && stock.imageUrl) return stock.imageUrl;
  return localImages.value[activeImageIndex.value] || "";
});

const close = () => {
  // Business rule: if a Declinaison is selected, a Pack must also be selected
  // Prevent closing the modal in this invalid state
  const p: any = localProduct.value || {};
  const declinaisonId = p.declinaisonId ?? p.id_declinaison;
  const packId = p.packId ?? p.id_pack;
  const venduPar = (p.venduPar ?? p.vendu_par) as string | undefined;
  if (declinaisonId && !packId && venduPar !== "tailles") {
    notifyError(
      "Veuillez sélectionner un Pack lorsque vous choisissez une Déclinaison.",
    );
    return;
  }
  emit("update:visible", false);
};

// ===== Photos depuis UploadPhoto =====
const buildPhotoUrl = (photo: string) => {
  return getProductImageUrl(photo);
};

const loadUploadPhotos = async (productId: number) => {
  try {
    const resp = await getProductPhotos(productId, "fiche");
    if (resp?.success) {
      const urls = (resp.photos || [])
        .map((p: string) => (typeof p === "string" ? p.trim() : ""))
        .filter(Boolean)
        .map(buildPhotoUrl);
      localImages.value = urls.length > 0 ? deduplicateUrls(urls) : [];
      activeImageIndex.value = 0;
    }
  } catch (e) {
    // Erreur silencieuse - les photos restent vides
  }
};

// Cache buster pour forcer le rechargement des images après rotate / set cover / delete
const photoCacheBuster = ref<Record<number, number>>({});
const setPhotoCacheBuster = (productId: number) => {
  // Persiste la version (localStorage) pour survivre au rafraîchissement et
  // invalider aussi le cache du CDN, puis garde une copie en mémoire.
  const version = bumpPhotoVersion(productId);
  photoCacheBuster.value = {
    ...photoCacheBuster.value,
    [productId]: version,
  };
};
const getDisplayPhotoUrl = (url: string): string => {
  if (!url) return "";
  const productId = productIdForPhotos.value;
  if (productId == null) return url;
  // Buster en mémoire (opération en cours) sinon version persistante (localStorage)
  const buster =
    photoCacheBuster.value[productId] ?? getPhotoVersion(productId);
  if (!buster) return url;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}v=${buster}`;
};

// Id produit pour les API photos (set cover, rotate, delete)
const productIdForPhotos = computed(() => {
  const p = localProduct.value as { id_produit?: number; id?: number } | null;
  if (!p) return null;
  return p.id_produit ?? p.id ?? null;
});

// État de chargement par action (comme gestion des photos avancée)
const photoActionState = ref<Record<string, boolean>>({});
const setPhotoActionState = (key: string, loading: boolean) => {
  if (loading) {
    photoActionState.value = { ...photoActionState.value, [key]: true };
  } else {
    const next = { ...photoActionState.value };
    delete next[key];
    photoActionState.value = next;
  }
};
const isPhotoActionLoading = (filename: string, action: string): boolean => {
  const id = productIdForPhotos.value;
  if (id == null) return false;
  const key = `${id}:${filename}:${action}`;
  return Boolean(photoActionState.value[key]);
};

// Modal suppression photo
const showDeletePhotoModal = ref(false);
const photoPendingDeletion = ref<string | null>(null);

const handleSetAsCover = async (photoUrl: string) => {
  if (isMainPhotoUrl(photoUrl)) return;
  const pid = productIdForPhotos.value;
  if (pid == null) return;
  const currentPositions = localImages.value
    .map(getPhotoPositionFromUrl)
    .filter(Boolean);
  const selectedPosition = getPhotoPositionFromUrl(photoUrl);
  const coverPosition = "c";
  const sortedPositions = [...currentPositions].sort((a, b) => {
    if (a === "c") return -1;
    if (b === "c") return 1;
    const numA = parseInt(a.replace("z-", ""), 10) || 0;
    const numB = parseInt(b.replace("z-", ""), 10) || 0;
    return numA - numB;
  });
  const positionsInNewOrder = sortedPositions.map((pos) => {
    if (pos === coverPosition) return selectedPosition;
    if (pos === selectedPosition) return coverPosition;
    return pos;
  });
  const idxC = localImages.value.findIndex(isMainPhotoUrl);
  const idxSel = localImages.value.indexOf(photoUrl);
  const previousOrder = [...localImages.value];
  if (idxC !== -1 && idxSel !== -1) {
    const arr = [...localImages.value];
    [arr[idxC], arr[idxSel]] = [arr[idxSel], arr[idxC]];
    localImages.value = arr;
    activeImageIndex.value = idxC;
  }
  const key = `${pid}:${getPhotoFilename(photoUrl)}:setCover`;
  setPhotoActionState(key, true);
  try {
    await reorderProductPhotos(pid, positionsInNewOrder);
    setPhotoCacheBuster(pid);
    await loadUploadPhotos(pid);
    if (localImages.value[0]) {
      emit("pending-cover", {
        productId: pid,
        coverUrl: localImages.value[0],
        nbPhotos: localImages.value.length,
      });
    }
    notification.success?.("Photo de couverture enregistrée");
  } catch (e) {
    localImages.value = previousOrder;
    activeImageIndex.value = localImages.value.indexOf(photoUrl);
    if (activeImageIndex.value < 0) activeImageIndex.value = 0;
    notifyError(
      e instanceof Error
        ? e.message
        : "Erreur lors du changement de couverture",
    );
  } finally {
    setPhotoActionState(key, false);
  }
};

const handleRotatePhoto = async (photoUrl: string) => {
  const filename = getPhotoFilename(photoUrl);
  if (!filename) {
    notifyError("Impossible de déterminer le fichier à pivoter");
    return;
  }
  const pid = productIdForPhotos.value;
  if (pid == null) return;
  const key = `${pid}:${filename}:rotate`;
  setPhotoActionState(key, true);
  try {
    await rotateProductPhoto(pid, filename, "clockwise");
    setPhotoCacheBuster(pid);
    await loadUploadPhotos(pid);
    notification.success?.("Rotation enregistrée");
  } catch (e) {
    notifyError(
      e instanceof Error ? e.message : "Erreur lors de la rotation de la photo",
    );
  } finally {
    setPhotoActionState(key, false);
  }
};

const getPendingRotationDegrees = (): number => 0;

const handleDownloadSinglePhoto = async (photoUrl: string) => {
  try {
    const filename = getPhotoFilename(photoUrl);
    if (!filename) {
      notifyError("Impossible de déterminer le fichier à télécharger");
      return;
    }
    const productId = productIdForPhotos.value;
    if (productId == null) {
      notifyError("Impossible de déterminer le produit à télécharger");
      return;
    }

    const imgRes = await fetchRest(
      `${getApiBaseUrl()}/api/product-photos/${productId}/original/${encodeURIComponent(
        filename,
      )}`,
      { method: "GET" },
    );
    if (!imgRes.ok) throw new Error("Impossible de télécharger la photo");
    const blob = await imgRes.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    notification.success?.("Photo téléchargée avec succès");
  } catch (e) {
    notifyError(
      e instanceof Error
        ? e.message
        : "Erreur lors du téléchargement de la photo",
    );
  }
};

const handleDownloadAllPhotos = async (productId: number) => {
  try {
    const base = getApiBaseUrl();
    // 1. Récupérer la liste des photos
    const listRes = await fetchRest(
      `${base}/images/product-photos/${productId}`,
      { method: "GET" },
    );
    if (!listRes.ok)
      throw new Error("Impossible de récupérer la liste des photos");
    const data = await listRes.json();
    const photos: string[] = data?.photos ?? [];
    if (photos.length === 0) {
      notifyError("Aucune photo disponible pour ce produit");
      return;
    }

    // 2. Télécharger chaque photo individuellement depuis le storage
    for (const photoPath of photos) {
      const filename = photoPath.split("/").pop()!;
      const imgRes = await fetchRest(getProductOriginalImageUrl(photoPath), {
        method: "GET",
      });
      if (!imgRes.ok) continue;
      const blob = await imgRes.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
    notification.success?.("Photos téléchargées avec succès");
  } catch (e) {
    notifyError(
      e instanceof Error
        ? e.message
        : "Erreur lors du téléchargement des photos",
    );
  }
};

const handleDeletePhoto = (photoUrl: string, index?: number) => {
  if (
    ((typeof index === "number" && index === 0) || isMainPhotoUrl(photoUrl)) &&
    localProduct.value?.premel !== "1"
  ) {
    notifyError("Impossible de supprimer la photo de couverture.");
    return;
  }
  if (
    typeof index === "number" &&
    index >= 0 &&
    index < localImages.value.length
  ) {
    activeImageIndex.value = index;
  }
  photoPendingDeletion.value = photoUrl;
  showDeletePhotoModal.value = true;
};

const isDeletingPhoto = ref(false);
const confirmDeletePhoto = async () => {
  const photoUrl = photoPendingDeletion.value;
  if (!photoUrl) {
    showDeletePhotoModal.value = false;
    photoPendingDeletion.value = null;
    return;
  }
  if (isMainPhotoUrl(photoUrl) && localProduct.value?.premel !== "1") {
    notifyError("Impossible de supprimer la photo de couverture.");
    showDeletePhotoModal.value = false;
    photoPendingDeletion.value = null;
    return;
  }
  const filename = getPhotoFilename(photoUrl);
  if (!filename) {
    notifyError("Impossible de déterminer le fichier à supprimer");
    showDeletePhotoModal.value = false;
    photoPendingDeletion.value = null;
    return;
  }
  const pid = productIdForPhotos.value;
  if (pid == null) {
    showDeletePhotoModal.value = false;
    photoPendingDeletion.value = null;
    return;
  }
  isDeletingPhoto.value = true;
  try {
    const deletedIndex = localImages.value.findIndex(
      (url) => normalizeUrlForDedup(url) === normalizeUrlForDedup(photoUrl),
    );
    const response = await deleteProductPhoto(pid, filename);
    // Appliquer d'abord la liste réorganisée renvoyée par le backend pour ne
    // plus référencer les fichiers supprimés/renommés, puis bumper le cache.
    if (Array.isArray(response.photos)) {
      const urls = response.photos
        .map((p: string) => (typeof p === "string" ? p.trim() : ""))
        .filter(Boolean)
        .map(buildPhotoUrl);
      localImages.value = urls.length > 0 ? deduplicateUrls(urls) : [];
    } else {
      await loadUploadPhotos(pid);
    }
    const nextIndex = deletedIndex >= 0 ? deletedIndex : activeImageIndex.value;
    activeImageIndex.value = localImages.value.length
      ? Math.min(nextIndex, localImages.value.length - 1)
      : 0;
    // La liste correcte est maintenant affichée : on rafraîchit le cache
    // (navigateur/CDN) des fichiers dont le contenu a pu changer.
    setPhotoCacheBuster(pid);
    emit("pending-cover", {
      productId: pid,
      coverUrl: localImages.value[0] || "",
      nbPhotos: localImages.value.length,
    });
    showDeletePhotoModal.value = false;
    photoPendingDeletion.value = null;
    notification.success?.("Photo supprimée");
  } catch (e) {
    notifyError(
      e instanceof Error
        ? e.message
        : "Erreur lors de la suppression de la photo",
    );
  } finally {
    isDeletingPhoto.value = false;
  }
};

// Zone d'upload
const isUploadingPhotos = ref(false);
const isDragOverUpload = ref(false);

const handleUploadPhotos = async (filesInput: FileList | File[]) => {
  const productId = productIdForPhotos.value;
  if (productId == null) return;
  const filesArray = Array.isArray(filesInput)
    ? filesInput
    : Array.from(filesInput ?? []);
  if (!filesArray.length) return;

  const validFiles = filesArray.filter((file) =>
    isSupportedProductPhotoFormat(file),
  );
  if (validFiles.length === 0) {
    notifyError(getUnsupportedProductPhotoErrorMessage());
    return;
  }
  if (validFiles.length !== filesArray.length) {
    notifyError(
      "Certaines photos ont été ignorées (format non pris en charge).",
    );
  }

  isUploadingPhotos.value = true;
  try {
    const response = await uploadProductPhotos(productId, validFiles);
    if (response?.success) {
      setPhotoCacheBuster(productId);
      await loadUploadPhotos(productId);

      const stock = localStocks.value[activeStockIndex.value];
      if (stock) {
        stock.imageUrl = localImages.value[0] || "";
      }

      if (localImages.value[0]) {
        emit("pending-cover", {
          productId,
          coverUrl: localImages.value[0],
          nbPhotos: localImages.value.length,
        });
      }

      notification.success?.(
        response.message || "Photos uploadées avec succès",
      );
    } else {
      notifyError(response?.message || "Erreur lors de l'upload des photos");
    }
  } catch (e) {
    notifyError(
      e instanceof Error ? e.message : "Erreur lors de l'upload des photos",
    );
  } finally {
    isUploadingPhotos.value = false;
  }
};

const triggerUploadFileDialog = () => {
  if (productIdForPhotos.value == null) return;
  const input = document.createElement("input");
  input.type = "file";
  input.accept = productPhotoInputAccept;
  input.multiple = true;
  input.addEventListener("change", (event) => {
    const target = event.target as HTMLInputElement;
    if (target?.files && target.files.length > 0) {
      handleUploadPhotos(target.files);
    }
    target.value = "";
    input.remove();
  });
  input.click();
};

const handleUploadDragEnter = (event: DragEvent) => {
  event.preventDefault();
  if (event.dataTransfer?.types.includes("Files")) {
    isDragOverUpload.value = true;
  }
};

const handleUploadDragOver = (event: DragEvent) => {
  event.preventDefault();
  if (event.dataTransfer?.types.includes("Files")) {
    isDragOverUpload.value = true;
  }
};

const handleUploadDragLeave = (event: DragEvent) => {
  event.preventDefault();
  const relatedTarget = event.relatedTarget as HTMLElement | null;
  const currentTarget = event.currentTarget as HTMLElement | null;
  if (!currentTarget?.contains(relatedTarget)) {
    isDragOverUpload.value = false;
  }
};

const handleUploadDrop = (event: DragEvent) => {
  event.preventDefault();
  isDragOverUpload.value = false;
  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    handleUploadPhotos(files);
  }
};

// Navigation & swipe
const nextImage = () => {
  if (!localImages.value.length) return;
  activeImageIndex.value =
    (activeImageIndex.value + 1) % localImages.value.length;
};
const prevImage = () => {
  if (!localImages.value.length) return;
  activeImageIndex.value =
    (activeImageIndex.value - 1 + localImages.value.length) %
    localImages.value.length;
};
let touchStartX = 0;
const onTouchStart = (e: TouchEvent) => {
  touchStartX = e.changedTouches[0]?.clientX || 0;
};
const onTouchEnd = (e: TouchEvent) => {
  const endX = e.changedTouches[0]?.clientX || 0;
  const delta = endX - touchStartX;
  if (Math.abs(delta) > 30) {
    if (delta < 0) nextImage();
    else prevImage();
  }
};

const handleSave = async () => {
  if (!localProduct.value) return;

  const pAny: any = localProduct.value;
  const declinaisonId = pAny.declinaisonId ?? pAny.id_declinaison;
  const packId = pAny.packId ?? pAny.id_pack;
  const venduParSave = (pAny.venduPar ?? pAny.vendu_par) as string | undefined;
  const venduParChanged =
    initialVenduPar.value != null &&
    venduParSave != null &&
    venduParSave !== initialVenduPar.value;

  // Déclinaison obligatoire
  if (!declinaisonId) {
    notifyError("Veuillez sélectionner une Déclinaison avant d'enregistrer.");
    return;
  }

  // Pack obligatoire si déclinaison choisie et type != tailles
  if (!packId && venduParSave !== "tailles") {
    notifyError(
      "Veuillez sélectionner un Pack lorsque vous choisissez une Déclinaison.",
    );
    return;
  }

  // Une simple modification de fiche conserve le stock existant. La validation
  // complète n'est nécessaire que lorsque l'utilisateur touche réellement au stock.
  if (stockModified.value) {
    if (venduParSave === "melangees") {
      if (localMelangeesStocks.value.length === 0) {
        notifyError("Aucune couleur renseignée pour ce produit mélangé.");
        return;
      }
      // Toutes les tailles de toutes les couleurs doivent être renseignées (>= 0)
      for (const row of localMelangeesStocks.value) {
        const missing = row.quantities.find((qs) => qs.quantity == null);
        if (missing) {
          notifyError(
            `Veuillez renseigner la quantité pour la taille "${missing.taille}" de la couleur "${row.colorLabel}".`,
          );
          return;
        }
      }
      // Somme par taille = quantités du pack sélectionné
      const packIdMel = pAny.packId ?? pAny.id_pack;
      const selectedPackMel = packs.value.find(
        (p) => String(p.id_pack) === String(packIdMel),
      );
      if (selectedPackMel?.valeur) {
        const packQuantites = parseDescriptionPaquet(selectedPackMel.valeur);
        const sums = new Array(packQuantites.length).fill(0);
        for (const row of localMelangeesStocks.value) {
          row.quantities.forEach((qs, i) => {
            if (i < sums.length) sums[i] += qs.quantity ?? 0;
          });
        }
        if (packQuantites.some((q, i) => q !== sums[i])) {
          notifyError(
            `La somme des quantités des couleurs (${formatDescriptionPaquet(sums)}) ne correspond pas aux quantités du paquet (${formatDescriptionPaquet(packQuantites)})`,
          );
          return;
        }
      }
    } else if (venduParSave === "couleurs") {
      if (!localStocks.value.some((s) => s.quantity != null)) {
        notifyError("Veuillez renseigner le stock pour au moins une couleur.");
        return;
      }
    } else if (venduParSave === "tailles") {
      if (
        !localTailleStocks.value.some((c) =>
          c.tailleStocks.some((ts) => ts.quantity != null),
        )
      ) {
        notifyError("Veuillez renseigner le stock pour au moins une taille.");
        return;
      }
    }
  }

  if (
    compositionType.value === "chaussures" &&
    String(pAny.dimensions || "").trim() &&
    !isValidProductDimensions(pAny.dimensions)
  ) {
    notifyError(
      "Les dimensions du colis doivent être au format longueur x largeur x hauteur, en cm.",
    );
    return;
  }

  const caracCount = selectedCaracteristiques.value.length;
  if (caracCount > 5) {
    notifyError(
      `Veuillez sélectionner au maximum 5 caractéristiques (actuellement : ${caracCount}).`,
      6000,
    );
    return;
  }

  // Valider les pourcentages uniquement pour le type "autres" (pas pour "chaussures" qui utilise des checkboxes)
  if (compositionType.value === "autres") {
    const invalidGroups = groupedCompositions.value.filter(
      (g) => g.total !== 100,
    );

    if (invalidGroups.length) {
      const detailsLines = invalidGroups.map(
        (g) => `${g.famille} : ${g.total}%`,
      );

      const message =
        "Veuillez saisir le pourcentage de chaque composition.\n" +
        "Pour chaque bloc (Produit, Doublure, Anse, ...), la somme doit être égale à 100%.\n\n" +
        detailsLines.join("\n");

      notifyError(message, 8000);
      return;
    }
  }

  const parsedPrixReduit = parsePrixReduitInput(prixReduitInput.value);
  if (parsedPrixReduit === undefined) {
    notifyError("Veuillez saisir un prix réduit valide.", 5000);
    return;
  }
  if (parsedPrixReduit != null && parsedPrixReduit <= 0) {
    notifyError("Le prix réduit doit être supérieur à 0.", 5000);
    return;
  }
  localProduct.value.prixReduit = parsedPrixReduit;
  const openedIsPromotion =
    openedPrixReduit.value != null && openedPrixReduit.value > 0;
  if (parsedPrixReduit != null) {
    localProduct.value.promotion = parsedPrixReduit;
    prixReduitInput.value = parsedPrixReduit.toFixed(2);
  } else if (openedIsPromotion) {
    localProduct.value.promotion = undefined;
  } else if (localPromotion.value != null && localPromotion.value > 0) {
    localProduct.value.promotion = localPromotion.value;
  } else {
    // 0 n'est pas une promotion : ne pas le réécrire.
    localProduct.value.promotion = undefined;
  }

  // Validation: prix réduit doit être inférieur au prix
  const prixReduitVal = localProduct.value.prixReduit;
  const prixVal = localProduct.value.prix;
  if (
    prixReduitVal != null &&
    prixVal != null &&
    prixReduitVal >= prixVal
  ) {
    notifyError("Le prix réduit doit être inférieur au prix.", 5000);
    return;
  }

  const poidsError = getMelUnitWeightError(localProduct.value.poids);
  if (poidsError) {
    notifyError(poidsError, 5000);
    return;
  }

  // S'assurer que id_declinaison et id_pack sont bien envoyés à l'API
  const p = localProduct.value as any;
  if (p.declinaisonId && !p.id_declinaison) {
    p.id_declinaison = p.declinaisonId;
  }
  if (p.packId && !p.id_pack) {
    p.id_pack = p.packId;
  }
  if (venduParSave === "tailles") {
    p.id_pack = null;
    p.packId = null;
  }

  // calculer catégorie
  let id_categorie: number | null = null;
  const lp: any = localProduct.value;
  if (lp.catLevel3Id) id_categorie = lp.catLevel3Id;
  else if (lp.catLevel2Id) id_categorie = lp.catLevel2Id;
  else if (lp.catLevel1Id) id_categorie = lp.catLevel1Id;

  // Une fiche en ligne ne peut pas être sauvegardée sans catégorie. Cette
  // validation doit précéder updateProduitCaracteristiques : sinon le clic
  // sur Enregistrer retirait la catégorie avant que ProductListView ne puisse
  // refuser le patch principal.
  if (lp.visible && !id_categorie) {
    notifyError(
      "Une catégorie est obligatoire pour enregistrer un produit en ligne.",
    );
    return;
  }
  try {
    // Use new compositionFormData format (unified with ListMelView)
    const items = compositionFormData.value
      .filter((c) => c.compositionId > 0)
      .map((c) => ({
        id_composition: c.compositionId,
        id_composition_localisation: c.localisationId,
        value: typeof c.percentage === "number" ? c.percentage : 0,
      }));

    await saveProduitCompositions(localProduct.value.id, items);

    // Auto-traduction de secours : si une description a été saisie mais que
    // l'utilisateur n'a pas cliqué sur "AI Traduction", on lance la traduction
    // automatiquement avant l'enregistrement.
    // On accepte aussi le cas où l'utilisateur a saisi le texte directement
    // dans l'un des champs de langue (sans passer par le champ principal).
    {
      let srcText = descriptionInput.value?.trim();
      if (!srcText) {
        srcText = (
          descriptionFr.value ||
          descriptionEn.value ||
          descriptionIt.value ||
          descriptionEs.value ||
          descriptionDe.value ||
          ""
        ).trim();
      }
      const filledCount = [
        descriptionFr.value,
        descriptionEn.value,
        descriptionIt.value,
        descriptionEs.value,
        descriptionDe.value,
      ].filter((v) => (v || "").trim()).length;
      if (srcText && filledCount < ALL_DESC_LANGS.length) {
        if (containsEmoji(srcText)) {
          notifyError(
            "La description ne doit pas contenir d'emojis : ils ne s'affichent pas correctement sur la fiche produit et perturbent la traduction automatique.",
            8000,
          );
          return;
        }
        descriptionInput.value = srcText;
        await autoTranslateDescription();
      }
    }

    const nlToBr = (s: string) => s.replace(/\n/g, "<br/>");
    await saveProduitDescription({
      id_produit: localProduct.value.id,
      texte_fr: nlToBr(descriptionFr.value),
      texte_uk: nlToBr(descriptionEn.value),
      texte_it: nlToBr(descriptionIt.value),
      texte_es: nlToBr(descriptionEs.value),
      texte_de: nlToBr(descriptionDe.value),
      texte_zh: nlToBr(descriptionZh.value),
    });
    // Vider le champ source "Détection automatique" après l'enregistrement
    descriptionInput.value = "";
    // Build stock items depending on venduPar
    const venduParForStock = venduParSave;
    let stockItems: Array<{
      id_couleur: number;
      value: number;
      taille: string | null;
    }> = [];

    if (venduParForStock === "tailles") {
      // Mode tailles: save per couleur × taille
      for (const c of localTailleStocks.value) {
        for (const ts of c.tailleStocks) {
          if (ts.quantity != null && ts.quantity >= 0) {
            stockItems.push({
              id_couleur: c.id_couleur,
              value: ts.quantity,
              taille: ts.taille,
            });
          }
        }
      }
    } else if (venduParForStock !== "melangees") {
      stockItems = localStocks.value
        .filter((s) => s.quantity != null && s.quantity >= 0)
        .map((s) => ({
          id_couleur: s.id_couleur,
          value: s.quantity!,
          taille: null,
        }));
    }

    const shouldSaveStock = stockModified.value;

    const anyPOrig = (props.product || {}) as any;
    // La composition mélangée ne modifie pas le nombre de paquets.
    // Ce nombre n'est enregistré que si le champ « Stock en paquet » change.
    const keepExistingStock = !shouldSaveStock || venduParForStock === "melangees";
    let finalStockValue = keepExistingStock
      ? venduParChanged
        ? 0
        : anyPOrig.stock_value ?? p.stock_value ?? 0
      : stockItems.reduce((acc, item) => acc + (item.value || 0), 0);

    let finalStockRenseigne = keepExistingStock
      ? venduParChanged
        ? false
        : anyPOrig.stock_renseigne ?? p.stock_renseigne ?? false
      : stockItems.length > 0;

    const packStockToSave =
      venduParForStock === "melangees" &&
      melangeesPackStockModified.value &&
      melangeesPackStock.value != null &&
      melangeesPackStock.value >= 0
        ? Math.trunc(melangeesPackStock.value)
        : null;
    if (packStockToSave != null) {
      finalStockValue = packStockToSave;
      finalStockRenseigne = true;
    }

    p.stock_value = finalStockValue;

    if (p.statut !== undefined) {
      if (!p.visible) {
        p.statut = "hors-ligne";
      } else {
        p.statut =
          finalStockRenseigne && finalStockValue === 0
            ? "rupture"
            : "en-vente";
      }
    }

    // Préparer le payload avec toutes les données mises à jour
    const payload: any = {
      ...localProduct.value,
      compositions: compositionFormData.value.map((c) => ({
        id: c.compositionId,
        localisationId: c.localisationId,
        libelle: c.compositionName,
        percentage: c.percentage,
      })),
      caracteristiques: selectedCaracteristiques.value,
      description_fr: descriptionFr.value.replace(/\n/g, "<br/>"),
      description_en: descriptionEn.value.replace(/\n/g, "<br/>"),
      description_it: descriptionIt.value.replace(/\n/g, "<br/>"),
      description_es: descriptionEs.value.replace(/\n/g, "<br/>"),
      description_de: descriptionDe.value.replace(/\n/g, "<br/>"),
      description_zh: descriptionZh.value.replace(/\n/g, "<br/>"),
      stock_value: finalStockValue,
      stock_renseigne: finalStockRenseigne,
      statut: p.statut,
      venduParChanged,
    };

    // Sauvegarder d'abord le produit (propagation vendu_par + purge stock famille côté API)
    await new Promise<void>((resolve, reject) => {
      emit("save", {
        ...payload,
        __resolveProductSave: resolve,
        __rejectProductSave: reject,
      });
    });

    // Le produit est maintenant enregistré. Mettre à jour les caractéristiques
    // ensuite évite de laisser une catégorie orpheline si la sauvegarde
    // principale est refusée (référence vide, catégorie d'une famille en
    // ligne, doublon, etc.).
    await updateProduitCaracteristiques({
      id_produit: localProduct.value.id,
      id_categorie,
      ids_caracteristiques: selectedCaracteristiques.value,
    });

    // Puis enregistrer le nouveau stock du produit courant
    if (shouldSaveStock) {
      if (venduParForStock === "couleurs") {
        // Mode couleurs: sync like ProductListView's syncToGestionDeStock
        const idDeclinaison = pAny.declinaisonId ?? pAny.id_declinaison;
        let syncedViaTaille = false;

        if (idDeclinaison) {
          const declinaison = await getDeclinaisonById(idDeclinaison);
          if (declinaison) {
            const tailles = [
              declinaison.d1_FR,
              declinaison.d2_FR,
              declinaison.d3_FR,
              declinaison.d4_FR,
              declinaison.d5_FR,
              declinaison.d6_FR,
              declinaison.d7_FR,
              declinaison.d8_FR,
              declinaison.d9_FR,
              declinaison.d10_FR,
              declinaison.d11_FR,
              declinaison.d12_FR,
            ].filter((t): t is string => !!t && t.trim() !== "");
            const uniqueTailles = [...new Set(tailles)];

            if (uniqueTailles.length === 1) {
              const taille = uniqueTailles[0];
              for (const item of stockItems) {
                await upsertProduitStock(
                  localProduct.value.id,
                  item.id_couleur,
                  item.value,
                  taille,
                );
              }
              syncedViaTaille = true;
            }
          }
        }

        if (!syncedViaTaille) {
          const existingStocks = await getProduitStocks(localProduct.value.id);

          for (const item of stockItems) {
            const existingForColor = existingStocks.filter(
              (s) => s.id_couleur === item.id_couleur,
            );

            if (existingForColor.length === 1 && existingForColor[0].taille) {
              await upsertProduitStock(
                localProduct.value.id,
                item.id_couleur,
                item.value,
                existingForColor[0].taille,
              );
            } else if (existingForColor.length === 0) {
              await upsertProduitStock(
                localProduct.value.id,
                item.id_couleur,
                item.value,
                null,
              );
            } else {
              await upsertProduitStock(
                localProduct.value.id,
                item.id_couleur,
                item.value,
                existingForColor[0].taille ?? null,
              );
            }
          }
        }
      } else if (venduParForStock !== "melangees") {
        await saveProduitStocks({
          id_produit: localProduct.value.id,
          items: stockItems,
        });
      }

      if (venduParForStock === "melangees") {
        for (const row of localMelangeesStocks.value) {
          await updateCouleurDescriptionPaquet(
            localProduct.value.id,
            row.id_couleur,
            formatDescriptionPaquet(row.quantities.map((qs) => qs.quantity)),
          );
        }
      }

      if (venduParForStock !== "melangees") {
        const stocksForList =
          venduParForStock === "tailles"
            ? localTailleStocks.value.map((c) => ({
                id_couleur: c.id_couleur,
                colorLabel: c.colorLabel,
                hexColor: c.hexColor,
                quantity: c.tailleStocks.reduce(
                  (sum, ts) => sum + (ts.quantity ?? 0),
                  0,
                ),
              }))
            : localStocks.value.map((s) => ({
                id_couleur: s.id_couleur,
                colorLabel: s.colorLabel,
                hexColor: s.hexColor,
                quantity: s.quantity,
              }));

        emit("stocks-updated", {
          id_produit: localProduct.value.id,
          stock_value: finalStockValue,
          stock_renseigne: finalStockRenseigne,
          stocks: stocksForList,
        });
      }
    }

    if (packStockToSave != null) {
      await saveProduitStocks({
        id_produit: localProduct.value.id,
        items: [
          {
            id_couleur: ID_COULEUR_MELANGEES,
            value: packStockToSave,
            taille: null,
          },
        ],
      });
      emit("stocks-updated", {
        id_produit: localProduct.value.id,
        stock_value: packStockToSave,
        stock_renseigne: true,
        stocks: [
          {
            id_couleur: ID_COULEUR_MELANGEES,
            colorLabel: "",
            quantity: packStockToSave,
          },
        ],
      });
    }

    if (venduParChanged) {
      initialVenduPar.value = venduParSave ?? null;
    }
  } catch (e) {
    return;
  }

  emit("update:visible", false);
};

const onImageError = (event: Event) => {
  const target = event.target as HTMLImageElement | null;
  if (target) {
    // Remove the failed URL from the list to avoid blank thumbnails
    const failed = target.currentSrc || target.src;
    const normalize = (u: string) => (u || "").split("?")[0];
    const beforeLen = localImages.value.length;
    localImages.value = localImages.value.filter(
      (u) => normalize(u) !== normalize(failed),
    );
    if (localImages.value.length === 0) {
      activeImageIndex.value = 0;
    } else if (activeImageIndex.value >= localImages.value.length) {
      activeImageIndex.value = 0;
    }
  }
};

// Compositions - Unified with ListMelView using CompositionForm
const handleCompositionInput = async (
  value: string,
  localisationId: string | null,
) => {
  const term = value.trim();

  if (term.length < 1) {
    showCompositionDropdown.value = false;
    compositionAutocompleteOptions.value = [];
    return;
  }

  try {
    isLoadingCompositionAutocomplete.value = true;

    // Filter only compositions from the SAME localisation
    const selectedCompositionIds = compositionFormData.value
      .filter((comp: any) =>
        localisationId
          ? comp.localisationId === parseInt(localisationId)
          : true,
      )
      .map((comp: any) => comp.compositionId)
      .filter((id: number) => id > 0)
      .join("#");

    const options = await getCompositionAutocomplete(
      term,
      selectedCompositionIds,
    );
    compositionAutocompleteOptions.value = options;
    showCompositionDropdown.value = options.length > 0;
  } catch (err) {
    console.error("Erreur autocomplete compositions:", err);
    compositionAutocompleteOptions.value = [];
    showCompositionDropdown.value = false;
  } finally {
    isLoadingCompositionAutocomplete.value = false;
  }
};

const selectComposition = (composition: { id: number; label: string }) => {
  showCompositionDropdown.value = false;
  compositionAutocompleteOptions.value = [];
};

const hideCompositionDropdown = () => {
  setTimeout(() => {
    showCompositionDropdown.value = false;
  }, 200);
};

// Initialize composition data based on product category
const initCompositionData = async () => {
  // Ensure reference data is loaded before initializing
  if (allCompositionsData.value.length === 0) {
    await loadInitialData();
  }

  const anyP = localProduct.value as any;
  const idCategorie =
    anyP?.id_categorie || anyP?.catLevel3Id || anyP?.catLevel2Id;

  // Determine composition type based on category
  compositionType.value = isShoeCategory(idCategorie) ? "chaussures" : "autres";

  // Load localisations and compositions based on type
  if (compositionType.value === "autres") {
    compositionLocalisations.value = allCompositionsData.value
      .filter(
        (comp) => comp.id_categorie === null && comp.type === "localisation",
      )
      .map((comp) => ({
        id_composition_localisation: comp.id,
        libelle: comp.libelle,
      }));
    compositionsList.value = allCompositionsData.value
      .filter(
        (comp) => comp.id_categorie === null && comp.type === "composition",
      )
      .map((comp) => ({
        id_composition: comp.id,
        libelle: comp.libelle,
      }));
  } else {
    compositionLocalisations.value = allCompositionsData.value
      .filter(
        (comp) => comp.id_categorie === "14" && comp.type === "localisation",
      )
      .map((comp) => ({
        id_composition_localisation: comp.id,
        libelle: comp.libelle,
      }));
    compositionsList.value = allCompositionsData.value
      .filter(
        (comp) => comp.id_categorie === "14" && comp.type === "composition",
      )
      .map((comp) => ({
        id_composition: comp.id,
        libelle: comp.libelle,
      }));
  }
};

// Legacy composition functions (kept for backward compatibility)
const addComposition = () => {
  if (!newCompositionFamilleId.value || !newCompositionId.value) return;

  const fam = compositionFamilies.value.find(
    (f) => f.id_composition_localisation === newCompositionFamilleId.value,
  );
  const opt = compositionOptions.value.find(
    (c) => c.id_composition === newCompositionId.value,
  );

  if (!fam || !opt) return;

  const exists = compositions.value.some(
    (c) =>
      c.id_composition === opt.id_composition &&
      c.familleId === fam.id_composition_localisation,
  );
  if (exists) return;

  compositions.value.push({
    id: Date.now(),
    famille: fam.libelle,
    familleId: fam.id_composition_localisation,
    libelle: opt.libelle,
    pourcentage: 0,
    id_composition: opt.id_composition,
  });

  newCompositionId.value = 0;
};
const removeComposition = (id: number) => {
  compositions.value = compositions.value.filter((c) => c.id !== id);
};

const groupedCompositions = computed(() => {
  // Use compositionFormData (new format) for validation
  const map = new Map<
    number,
    { localisationId: number; items: CompositionFormItem[]; total: number }
  >();

  for (const c of compositionFormData.value) {
    const locId = c.localisationId;
    if (!map.has(locId)) {
      map.set(locId, { localisationId: locId, items: [], total: 0 });
    }
    const group = map.get(locId)!;
    group.items.push(c);
    group.total += typeof c.percentage === "number" ? c.percentage : 0;
  }

  // Get localisation name helper
  const getLocName = (locId: number): string => {
    if (locId === 0) return "Produit";
    const loc = compositionLocalisations.value.find(
      (l) => l.id_composition_localisation === locId,
    );
    return loc ? loc.libelle : `Localisation ${locId}`;
  };

  return Array.from(map.values()).map((g) => ({
    famille: getLocName(g.localisationId),
    items: g.items,
    total: g.total,
  }));
});

// Caractéristiques
const isCaracteristiqueSelected = (id: number) =>
  selectedCaracteristiques.value.includes(id);

const toggleCaracteristique = (id: number) => {
  if (isCaracteristiqueSelected(id)) {
    selectedCaracteristiques.value = selectedCaracteristiques.value.filter(
      (x) => x !== id,
    );
  } else {
    if (selectedCaracteristiques.value.length >= 5) {
      notifyError(
        "Vous pouvez sélectionner au maximum 5 caractéristiques.",
        5000,
      );
      return;
    }
    selectedCaracteristiques.value = [...selectedCaracteristiques.value, id];
  }
};

const formattedDate = computed(() => {
  const d = props.product?.date;
  if (!d) return "";
  try {
    const dateObj =
      typeof d === "string" || typeof d === "number"
        ? new Date(d)
        : (d as Date);
    if (isNaN(dateObj.getTime())) return "";
    return dateObj.toLocaleDateString("fr-FR");
  } catch {
    return "";
  }
});

// Flag pour éviter de charger plusieurs fois
const isDataLoaded = ref(false);

const loadInitialData = async () => {
  if (isDataLoaded.value) return;

  try {
    const authStore = useAuthStore();
    const idVendeur = authStore.user?.id;

    const [families, comps, cols, refData, packsData, declData] =
      await Promise.all([
        getCompositionFamilies("fr"),
        getCompositions("fr"),
        getCollections("fr"),
        getReferenceData(),
        // Charger packs en parallèle si les props sont vides
        idVendeur && (!propsPacks.value || propsPacks.value.length === 0)
          ? getPacksByVendeur(idVendeur)
          : Promise.resolve(null),
        // Charger déclinaisons en parallèle si les props sont vides
        idVendeur &&
        (!propsDeclinaisons.value || propsDeclinaisons.value.length === 0)
          ? getDeclinaisonsByVendeur(idVendeur)
          : Promise.resolve(null),
      ]);

    compositionFamilies.value = families;
    if (families.length > 0) {
      newCompositionFamilleId.value = families[0].id_composition_localisation;
    }

    compositionOptions.value = comps;
    // Caracteristiques are now loaded dynamically based on product category
    collections.value = cols;

    // Store compositions data for CompositionForm (unified with ListMelView)
    if (refData && refData.compositions) {
      allCompositionsData.value = refData.compositions;
    }

    // Appliquer packs & déclinaisons si chargés
    if (packsData) localPacks.value = packsData;
    if (declData) localDeclinaisons.value = declData;

    isDataLoaded.value = true;
  } catch (e) {
    // Erreur silencieuse - les familles de composition restent vides
  }
};

onMounted(() => {
  window.addEventListener("keydown", onKeydown);
  // Ne pas charger les données au montage, attendre que le modal soit ouvert
});

// Watch for category changes to reload caracteristiques dynamically
watch(
  () => {
    const lp = localProduct.value as any;
    return (
      lp?.id_categorie || lp?.catLevel3Id || lp?.catLevel2Id || lp?.catLevel1Id
    );
  },
  async (newCategorie, oldCategorie) => {
    // Only reload if category actually changed (not on initial load)
    if (
      newCategorie &&
      oldCategorie !== undefined &&
      newCategorie !== oldCategorie
    ) {
      await loadCaracteristiquesByCategory(newCategorie);
      // Reset selected caracteristiques when category changes
      selectedCaracteristiques.value = [];
    }
  },
);

// =============== Collections & Catégories (cascade) =================
const collections = ref<CollectionOption[]>([]);

// =============== Vendu par / Déclinaison / Pack =====================

// Filtrer les packs en fonction du nombre de tailles de la déclinaison sélectionnée
const filteredPacks = computed(() => {
  const lp: any = localProduct.value || {};
  const idDecl = lp?.id_declinaison || lp?.declinaisonId;
  if (!idDecl) return [];
  const d = declinaisons.value.find(
    (x) => Number(x.id_declinaison) === Number(idDecl),
  );
  if (!d?.tailles) return packs.value;
  const sizeCount = getDeclinaisonLength(d.tailles);
  return packs.value.filter((p) => {
    return getPackLength(p.valeur) === sizeCount;
  });
});

// ----- Echo & sync for Vendu par / Déclinaison / Pack -----
const isInitializingVDP = ref(false);

async function initLocalVDP() {
  const lp: any = localProduct.value;
  if (!lp) return;

  // Wait a tick in case options are loading
  isInitializingVDP.value = true;

  // Normalize incoming field names from list/table or backend
  if (lp.vendu_par && !lp.venduPar) lp.venduPar = lp.vendu_par;

  if (lp.declinaisonId && !lp.id_declinaison)
    lp.id_declinaison = Number(lp.declinaisonId);
  if (lp.packId && !lp.id_pack) lp.id_pack = Number(lp.packId);

  // If values are strings, coerce to numbers, but treat 0/NaN as null (no selection)
  const normDecl =
    lp.id_declinaison != null && lp.id_declinaison !== ""
      ? Number(lp.id_declinaison)
      : null;
  lp.id_declinaison = normDecl && !isNaN(normDecl) ? normDecl : null;
  const normPack =
    lp.id_pack != null && lp.id_pack !== "" ? Number(lp.id_pack) : null;
  lp.id_pack = normPack && !isNaN(normPack) ? normPack : null;

  if (lp.venduPar === "tailles" || lp.vendu_par === "tailles") {
    lp.id_pack = null;
    lp.packId = null;
  }

  await nextTick();
  isInitializingVDP.value = false;
}

// Keep duplicate id fields in sync while editing (defensive)
watch(
  () => (localProduct.value as any)?.id_declinaison,
  (v) => {
    const lp: any = localProduct.value;
    if (!lp || isInitializingVDP.value) return;
    lp.declinaisonId = v ?? null;
    // If current pack no longer matches filtered set (size count mismatch), clear it
    // Only clear if packs are already loaded — avoid clearing during initial async load
    if (packs.value.length > 0) {
      const ok = filteredPacks.value.some(
        (p) => Number(p.id_pack) === Number(lp.id_pack),
      );
      if (!ok) lp.id_pack = null;
    }
    // Recharger les stocks en temps réel quand la déclinaison change
    if (lp.id_produit) {
      loadStocksForProduct(lp.id_produit);
    }
  },
);

watch(
  () => (localProduct.value as any)?.id_pack,
  (v) => {
    const lp: any = localProduct.value;
    if (!lp || isInitializingVDP.value) return;
    lp.packId = v ?? null;
  },
);

// Recharger le stock en paquet quand le type de vente change (admin)
watch(
  () => (localProduct.value as any)?.venduPar,
  (_v) => {
    const lp: any = localProduct.value;
    if (!lp || isInitializingVDP.value) return;
    if (lp.id_produit) {
      loadStocksForProduct(lp.id_produit);
    }
  },
);

// Catégories à 3 niveaux basées sur props.categoriesTree
const level1Categories = computed(() => (props.categoriesTree || []) as any[]);

const level2Categories = computed<any[]>(() => {
  const lp: any = localProduct.value || {};
  const id1 = lp?.catLevel1Id;
  if (!id1) return [];
  const found = (props.categoriesTree || []).find(
    (c: any) => c.id_categorie === id1,
  );
  return found?.children || [];
});

const level3Categories = computed<any[]>(() => {
  const lp: any = localProduct.value || {};
  const id1 = lp?.catLevel1Id;
  const id2 = lp?.catLevel2Id;
  if (!id1 || !id2) return [];
  const level1 = (props.categoriesTree || []).find(
    (c: any) => c.id_categorie === id1,
  );
  const level2 = level1?.children?.find((c: any) => c.id_categorie === id2);
  return level2?.children || [];
});

// Flag to avoid resetting fields while we programmatically initialize echo values
const isInitializingCategories = ref(false);

// Quand un niveau change, réinitialiser les suivants et mettre à jour id_categorie cible
watch(
  () => (localProduct.value as any)?.catLevel1Id,
  (v) => {
    const lp: any = localProduct.value;
    if (!lp) return;
    if (isInitializingCategories.value) {
      // During init we set catLevel1/2/3 together — do not auto-reset
      return;
    }
    if (!v) {
      lp.catLevel2Id = null;
      lp.catLevel3Id = null;
      lp.id_categorie = null;
      return;
    }
    lp.catLevel2Id = null;
    lp.catLevel3Id = null;
    lp.id_categorie = v;
  },
);

watch(
  () => (localProduct.value as any)?.catLevel2Id,
  (v) => {
    const lp: any = localProduct.value;
    if (!lp) return;
    if (isInitializingCategories.value) {
      return;
    }
    if (!v) {
      lp.catLevel3Id = null;
      lp.id_categorie = lp.catLevel1Id || null;
      return;
    }
    lp.catLevel3Id = null;
    lp.id_categorie = v;
  },
);

watch(
  () => (localProduct.value as any)?.catLevel3Id,
  (v) => {
    const lp: any = localProduct.value;
    if (!lp) return;
    if (isInitializingCategories.value) {
      return;
    }
    if (!v) {
      lp.id_categorie = lp.catLevel2Id || lp.catLevel1Id || null;
      return;
    }
    lp.id_categorie = v;
  },
);

// ===== Echo categories exactly like ProductListView =====
// Build a path index from props.categoriesTree so we can resolve any leaf id to its full path
const categoryPathIndex = ref<Record<number, number[]>>({});

const buildCategoryPathIndexFromProps = () => {
  const tree = (props.categoriesTree || []) as any[];
  const index: Record<number, number[]> = {};

  const dfs = (node: any, path: number[]) => {
    const newPath = [...path, node.id_categorie];
    index[node.id_categorie] = newPath;
    const children = node.children || [];
    for (const child of children) dfs(child, newPath);
  };

  for (const root of tree) dfs(root, []);
  categoryPathIndex.value = index;
};

// Initialize localProduct.catLevel1/2/3 from the deepest category id using the path index
async function initLocalProductCategories() {
  const lp: any = localProduct.value;
  if (!lp) return;
  if (!props.categoriesTree || (props.categoriesTree as any[]).length === 0)
    return;
  if (
    !categoryPathIndex.value ||
    Object.keys(categoryPathIndex.value).length === 0
  ) {
    buildCategoryPathIndexFromProps();
  }

  isInitializingCategories.value = true;
  const leafId: number | undefined =
    (lp.id_categorie ?? lp.catLevel3Id ?? lp.catLevel2Id ?? lp.catLevel1Id) ||
    undefined;
  if (!leafId) {
    // Un produit sans catégorie doit rester éditable : ne pas laisser le
    // verrou d'initialisation actif, sinon les watchers de la cascade ignorent
    // les sélections faites par l'utilisateur.
    isInitializingCategories.value = false;
    return;
  }

  const path = categoryPathIndex.value[leafId];
  if (!path || !path.length) {
    isInitializingCategories.value = false;
    return;
  }

  if (path.length === 1) {
    lp.catLevel1Id = path[0];
    lp.catLevel2Id = null;
    lp.catLevel3Id = null;
    lp.id_categorie = path[0];
  } else if (path.length === 2) {
    lp.catLevel1Id = path[0];
    lp.catLevel2Id = path[1];
    lp.catLevel3Id = null;
    lp.id_categorie = path[1];
  } else {
    // Use the last 3 levels to mirror list view behavior
    const l = path.length;
    lp.catLevel1Id = path[l - 3];
    lp.catLevel2Id = path[l - 2];
    lp.catLevel3Id = path[l - 1];
    lp.id_categorie = path[l - 1];
  }

  // Defer unblocking so computed options have time to update
  await nextTick();
  isInitializingCategories.value = false;
}

// Rebuild index and re-initialize when categories tree changes
watch(
  () => props.categoriesTree,
  () => {
    buildCategoryPathIndexFromProps();
    initLocalProductCategories();
    // Keep VDP echoed as well when reopening with new data
    initLocalVDP();
  },
  { deep: true },
);

// Also re-run right after localProduct is set (before heavy async loads)
watch(
  () => props.product,
  () => {
    // localProduct is already set by the previous watcher earlier in the file
    initLocalProductCategories();
    initLocalVDP();
  },
);
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease-out;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
